// WS-18: tree models, impostors, LOD cross-fade, snow load, seasons. Engine-generic (no resort data here).
//
// Why this exists (round 4d feedback, taxonomy #10): the WS-16 trees were faceted cones and icosahedron clumps
// with white-painted upward faces ("horrific blobs", "blocky close up", "polygon blocks that change quality when
// you zoom"). This module replaces the tree geometry, materials and LOD; placement stays in vegetation.js.
//
// Pipeline (all at load time, no downloaded assets, so nothing to license):
//  1. Texture atlas drawn on a canvas: pine needle tufts, spruce sprays, birch twigs (+ summer leaf mask), bark
//     strips. A second "aux" atlas: R = where snow settles (blurred along the shoots), G = leaf mask, B = AO.
//  2. Species models (unit tree: height 1, crown radius 1; instances scale them to the surveyed height and crown):
//     two directions, `cards` (realistic: branch skeleton + alpha-cut needle cards) and `stylised` (clean smooth
//     pads / skirts). Two variants per species.
//  3. Impostors: each variant is rendered from N x N directions over the upper hemisphere (hemi-octahedral
//     layout) into two atlases: albedo + coverage, and octahedral normal + AO + snow threshold. Far trees are one
//     quad that blends the 3 nearest frames (reprojected, so they line up). The view is mapped into the tree's
//     own normalised space (crown radius 1, half height 1), so tall thin trees use the full frame resolution.
//  4. Near trees (within `lod.near` m of the camera) use the 3D model; the hand-over is a dithered cross-fade over
//     `lod.band` m, complementary per pixel (each pixel is drawn by exactly one of the two), also in the shadow
//     pass. Nothing pops.
//  5. Snow is a load 0..1 compared with a per-texel threshold (snow capacity = settle mask x how much the card
//     faces up x shelter), so the same bake serves every snow amount, near and far; heavy loads grow lumps past
//     the needles (tykky). Season changes the leaves and needle tint and re-bakes the impostors.
//  6. Density: every tree has a stable rank (understorey first), the slider hides ranks above the amount.
//     Selection focus: trees between the camera and the selected place fade out (dithered; WS-19: fully, no hint).
// WS-75: WebGPURenderer + node materials (TSL). The impostor bake renders into render targets and the near trees,
// impostors and their sun shadows (castShadowPositionNode + maskShadowNode) are node materials with the same maths.
import * as THREE from 'three/webgpu';
import { Fn, uniform, uniformArray, attribute, texture, uv, varying, varyingProperty, float, int, bool, vec2, vec3, vec4, select, mix, smoothstep, step, clamp, max, min, pow, floor, fract, mod,
  exp, log2, sqrt, dot, cross as vcross, length, distance, normalize, dFdx, dFdy, fwidth, positionGeometry, positionView, normalLocal, normalViewGeometry, faceDirection, cameraPosition, cameraViewMatrix,
  screenCoordinate, instanceColor, diffuseColor, specularColor, specularColorBlended, specularF90, materialAlphaTest, depth,
  normalView, positionViewDirection, roughness, diffuseContribution, BRDF_GGX, BRDF_Lambert, F_Schlick, renderGroup, sRGBTransferOETF } from 'three/tsl';
import { AT, REG, uvRect, drawAtlas } from './trees-atlas.js';
import { deviceLost } from './gpu/internals.js'; // VIS-267: no readback on a lost device
import { dryDraw } from './gpu/pipelines.js';
import { lane } from './lane.js'; // VIS-267: a mean bake's pass waits its turn after Ready
import { ign, ignFrag } from './gpu/dither.js';
import { octEncY, octDecY, hemiEnc, hemiDec } from './gpu/octahedral.js';
import { iCol0, iCol1, iCol3, markShared, sharedInstances } from './gpu/instance.js';
import { onFog, SEAMS } from './gpu/chain.js';

// VIS-176: the take-over cross-fade (lazy/kittrees.js). One screen dither shared by today's trees and the kit trees:
// a pixel shows the kit tree once the share uHeroFade.x passes its threshold and today's tree of a kit species until
// then (complementary pixels; no per-tree seed, so the forest dissolves evenly). Share 0 = today's look, unchanged.
export const heroFadeQ = () => ign(screenCoordinate.yx.add(vec2(91.7, 23.3)));
/** Today's tree of variant vi steps aside for its kit tree: { gone (this pixel), goneShadow (half way: one clean switch) }. */
function heroGone(U, vi) {
  const sp = floor(vi.add(0.5).div(max(U.uLeafHq.w, 1))), F = U.uHeroFade;
  const isH = select(sp.lessThan(0.5), F.y, select(sp.lessThan(1.5), F.z, F.w)).greaterThan(0.5);
  return { gone: isH.and(heroFadeQ().lessThan(F.x)), goneShadow: isH.and(F.x.greaterThanEqual(0.5)) };
}
const hazedLand = new WeakSet(); // far ground materials that carry the canopy haze
const PLACEHOLDER = new THREE.DataTexture(new Uint8Array(4), 1, 1); PLACEHOLDER.needsUpdate = true; // until the first impostor bake
/** VIS-267: WebGPU done with the work sent so far, its frees included (the WebGL2 backend: at once). Also lazy/kittrees.js. */
export const gpuDone = (r) => r.backend?.device?.queue?.onSubmittedWorkDone?.() ?? Promise.resolve();

// VIS-233 phone set (owner 2026-10-21: half as many full 3D trees nearby): on a phone (createTreeLayer `mobile`) the 3D
// procedural trees (lod.near) hand over to their impostors at this share of the distance. The near trees stand on the
// ground round the camera, so their number goes with the distance squared. Same band, same cross-fade.
// Phone heat (owner 2026-10-22: the near 3D trees may go down to ~20 %): ~30 % of a computer's (was half, SQRT1_2);
// the far forest is unchanged. The kit trees have their own phone share (lazy/kittrees.js PHONE_KIT, sqrt(0.7): the
// owner's pick 2026-11-05); these stay at 0.3: built only when the kit fails, and heavier than the kit.
export const PHONE_NEAR = Math.sqrt(0.3);

// ------------------------------------------------------------------ seasons
// WS-18 r3: default snow-on-trees levels (vegetation.snow.levels overrides). heavy = the snow-laden crowns of the
// mid-winter drone photos (Drone Property 4/5/20, formerly ?preview=look-b) with that look's brighter grade.
export const SNOW_LEVELS = [
  { id: 'none', label: 'None', load: 0 },
  { id: 'light', label: 'Light', load: 0.25 },
  { id: 'medium', label: 'Medium', load: 0.45 },
  { id: 'heavy', label: 'Heavy', load: 0.65, gradeK: { hemi: 1.1, sun: 0.96 } }, // VIS-79: relative to the base grade (0.82 / 1.12 -> 0.9 / 1.08 as before)
];
export const SEASONS = {
  winter: { snowLoad: 0.35, foliage: 0, leaf: '#b8a040', needle: [1, 1, 1], groundSnow: 1 },
  spring: { snowLoad: 0, foliage: 0.45, leaf: '#9cbc4a', needle: [1.04, 1.08, 1.0], groundSnow: 0.15 },
  summer: { snowLoad: 0, foliage: 1, leaf: '#5f8a34', needle: [1.08, 1.16, 1.02], groundSnow: 0 },
  autumn: { snowLoad: 0, foliage: 0.85, leaf: '#d3a12a', needle: [1.0, 1.04, 0.98], groundSnow: 0 },
};
/** Season parameters: a named season, optionally overridden field by field (cfg.seasonParams). */
export function seasonParams(name = 'winter', over = {}) {
  return { ...(SEASONS[name] || SEASONS.winter), ...over, name: SEASONS[name] ? name : 'winter' };
}

// ------------------------------------------------------------------ small helpers
const mulberry = (a) => () => { a |= 0; a = (a + 0x6d2b79f5) | 0; let t = Math.imul(a ^ (a >>> 15), 1 | a); t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t; return ((t ^ (t >>> 14)) >>> 0) / 4294967296; };
const lerp = (a, b, t) => a + (b - a) * t;
const clamp01 = (x) => Math.min(1, Math.max(0, x));
const smooth = (e0, e1, x) => { const t = clamp01((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };
const hexRGB = (h) => { const c = new THREE.Color(h); return [c.r, c.g, c.b]; }; // linear

// ------------------------------------------------------------------ 1. texture atlas (web/js/trees-atlas.js)
// Drawn in a module worker (OffscreenCanvas) started as soon as the vegetation package loads, so it overlaps the
// terrain loading; main-thread fallback where workers or OffscreenCanvas are missing.
/** Start drawing the atlas (returns a Promise of the raw pixel data). hq = vegetation.trees.hq (its birch options change
 *  the birch twig fans and bark: trees-atlas.js; absent = today's atlas). */
const atlasOpt = (hq) => ({ birch: hq?.birch || {} });
export function prepareAtlas(hq) {
  const main = () => drawAtlas((w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }, atlasOpt(hq));
  if (typeof OffscreenCanvas === 'undefined' || typeof Worker === 'undefined') return Promise.resolve(main());
  return new Promise((res) => {
    let done = false;
    const fallback = () => { if (!done) { done = true; res(main()); } };
    try {
      const w = new Worker(new URL('./trees-atlas-worker.js', import.meta.url), { type: 'module' });
      w.onmessage = (e) => { if (done) return; done = true; w.terminate(); res(e.data?.map ? e.data : main()); };
      w.onerror = () => { w.terminate(); fallback(); };
      w.postMessage({ opt: atlasOpt(hq) });
    } catch { fallback(); }
  });
}
/** Raw atlas data -> { map, aux } DataTextures (map sRGB with straight alpha, aux linear). */
export function buildAtlas(data = prepareAtlasSync()) {
  const mkTex = (px, n, srgb) => {
    const t = new THREE.DataTexture(px, n, n, THREE.RGBAFormat, THREE.UnsignedByteType);
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter;
    t.anisotropy = 4; t.needsUpdate = true;
    return t;
  };
  return { map: mkTex(data.map, data.size, true), aux: mkTex(data.aux, data.auxSize, false), data };
}
function prepareAtlasSync(hq) { return drawAtlas((w, h) => { const c = document.createElement('canvas'); c.width = w; c.height = h; return c; }, atlasOpt(hq)); }

// ------------------------------------------------------------------ 2. geometry builder
// Attributes: position, normal (foliage: card normal blended with a crown-volume normal), uv, color (tint x AO),
// aCardN (true card normal: snow faces), aVeg = (snow capacity, foliage flag, wind weight: unread since the sway went, AO).
class Geo {
  constructor() { this.P = []; this.N = []; this.CN = []; this.UV = []; this.C = []; this.V = []; this.I = []; }
  get n() { return this.P.length / 3; }
  vert(p, nrm, cn, uv, col, veg) { this.P.push(p[0], p[1], p[2]); this.N.push(nrm[0], nrm[1], nrm[2]); this.CN.push(cn[0], cn[1], cn[2]); this.UV.push(uv[0], uv[1]); this.C.push(col[0], col[1], col[2]); this.V.push(veg[0], veg[1], veg[2], veg[3]); return this.n - 1; }
  /** Winding follows the card normal (front face = the side aCardN points to), so frontFacing tells top from bottom. */
  tri(a, b, c) {
    const P = this.P, C = this.CN, e1 = [P[b * 3] - P[a * 3], P[b * 3 + 1] - P[a * 3 + 1], P[b * 3 + 2] - P[a * 3 + 2]], e2 = [P[c * 3] - P[a * 3], P[c * 3 + 1] - P[a * 3 + 1], P[c * 3 + 2] - P[a * 3 + 2]];
    const n = cross(e1, e2), cn = [0, 1, 2].map((k) => C[a * 3 + k] + C[b * 3 + k] + C[c * 3 + k]);
    if (n[0] * cn[0] + n[1] * cn[1] + n[2] * cn[2] < 0) this.I.push(a, c, b); else this.I.push(a, b, c);
  }
  /** -> BufferGeometry in unit space (x,z / crownR, y / H). Normals transformed accordingly. */
  build(H, crownR) {
    const g = new THREE.BufferGeometry(), n = this.n;
    const P = new Float32Array(n * 3), N = new Float32Array(n * 3), CN = new Float32Array(n * 3);
    const tn = (a, i, out) => { const x = a[i] * crownR, y = a[i + 1] * H, z = a[i + 2] * crownR, l = Math.hypot(x, y, z) || 1; out[i] = x / l; out[i + 1] = y / l; out[i + 2] = z / l; }; // inverse transpose of diag(1/r, 1/H, 1/r)
    for (let i = 0; i < n * 3; i += 3) {
      P[i] = this.P[i] / crownR; P[i + 1] = this.P[i + 1] / H; P[i + 2] = this.P[i + 2] / crownR;
      tn(this.N, i, N); tn(this.CN, i, CN);
    }
    // WS-75: one interleaved vertex buffer (WebGPU allows 8 vertex buffers per draw; the instance data takes 4 more)
    const S = 18, X = new Float32Array(n * S);
    for (let i = 0; i < n; i++) {
      const o = i * S;
      X.set(P.subarray(i * 3, i * 3 + 3), o); X.set(N.subarray(i * 3, i * 3 + 3), o + 3); X.set(CN.subarray(i * 3, i * 3 + 3), o + 6);
      X[o + 9] = this.UV[i * 2]; X[o + 10] = this.UV[i * 2 + 1]; X[o + 11] = this.C[i * 3]; X[o + 12] = this.C[i * 3 + 1]; X[o + 13] = this.C[i * 3 + 2];
      X[o + 14] = this.V[i * 4]; X[o + 15] = this.V[i * 4 + 1]; X[o + 16] = this.V[i * 4 + 2]; X[o + 17] = this.V[i * 4 + 3];
    }
    const ib = new THREE.InterleavedBuffer(X, S);
    for (const [k, size, off] of [['position', 3, 0], ['normal', 3, 3], ['aCardN', 3, 6], ['uv', 2, 9], ['color', 3, 11], ['aVeg', 4, 14]]) g.setAttribute(k, new THREE.InterleavedBufferAttribute(ib, size, off));
    g.setIndex(n > 65535 ? new THREE.Uint32BufferAttribute(this.I, 1) : new THREE.Uint16BufferAttribute(this.I, 1));
    g.computeBoundingSphere();
    return g;
  }
}
const V3 = THREE.Vector3;
const norm = (v) => { const l = Math.hypot(v[0], v[1], v[2]) || 1; return [v[0] / l, v[1] / l, v[2] / l]; };
const cross = (a, b) => [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]];
const add = (a, b, s = 1) => [a[0] + b[0] * s, a[1] + b[1] * s, a[2] + b[2] * s];
const mixv = (a, b, t) => norm([lerp(a[0], b[0], t), lerp(a[1], b[1], t), lerp(a[2], b[2], t)]);

/** Tube along a polyline (bark): pts [[x,y,z],..], radii [..], sides; uv in a bark region (v by height / H). */
function tube(G, pts, radii, sides, reg, H, o = {}) {
  const rr = uvRect(reg), base = G.n, rows = pts.length;
  for (let i = 0; i < rows; i++) {
    const p = pts[i], q = pts[Math.min(rows - 1, i + 1)], pp = pts[Math.max(0, i - 1)];
    const t = norm([q[0] - pp[0], q[1] - pp[1], q[2] - pp[2]]);
    const ref = Math.abs(t[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0];
    const s = norm(cross(t, ref)), b = cross(t, s);
    for (let k = 0; k <= sides; k++) {
      const a = (k / sides) * Math.PI * 2, n = norm(add(add([0, 0, 0], s, Math.cos(a)), b, Math.sin(a)));
      const pos = add(p, n, radii[i]);
      const v = o.vFixed ?? lerp(rr.v0, rr.v1, clamp01(p[1] / H));
      const ao = o.ao ? o.ao(pos) : 1;
      const tint = o.col || [1, 1, 1];
      G.vert(pos, n, n, [lerp(rr.u0, rr.u1, (k / sides) * (o.uRep || 1) % 1.0001), v], [tint[0] * ao, tint[1] * ao, tint[2] * ao], [o.snow ?? 0.25, 0, o.wind ? o.wind(pos) : 0, ao]);
    }
  }
  for (let i = 0; i < rows - 1; i++) for (let k = 0; k < sides; k++) {
    const a = base + i * (sides + 1) + k, b = a + 1, c = a + sides + 1, d = c + 1;
    G.tri(a, c, b); G.tri(b, c, d);
  }
}

/** Folded ("tent") needle card along a direction: ridge from p0 along dir, sides slope down by `fold`, drooping
 *  tip. reg = atlas region (u along the card, v across, ridge at v = 0.5). vol(pos) -> crown-volume normal. */
function tent(G, p0, dir, len, halfW, fold, droop, reg, o) {
  const rr = uvRect(reg);
  const d = norm(dir), side0 = norm(cross([0, 1, 0], d).map((x) => -x)); // horizontal, to the card's left
  const segs = o.segs || 2, rows = [];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs, dp = droop * t * t;
    const c = add(add(p0, d, len * t), [0, 1, 0], -dp * len);
    const tang = norm(add(d, [0, -1, 0], 2 * droop * t));
    const up = norm(cross(side0.map((x) => -x), tang).map((x) => -x));
    const w = halfW * (o.shape ? o.shape(t) : 1);
    const L = add(add(c, side0, w * Math.cos(fold)), up, -w * Math.sin(fold));
    const Rt = add(add(c, side0, -w * Math.cos(fold)), up, -w * Math.sin(fold));
    rows.push({ c, L, Rt, up, t });
  }
  const sideN = (row, s) => norm(add(row.up, s === 0 ? side0 : side0.map((x) => -x), Math.tan(fold) * 0.9)); // the two faces of the tent
  for (const s of [0, 1]) {
    const ids = rows.map((row) => {
      const u = lerp(rr.u0, rr.u1, row.t);
      const pe = s === 0 ? row.L : row.Rt, cn = sideN(row, s);
      const ve = s === 0 ? rr.v1 : rr.v0, vc = (rr.v0 + rr.v1) / 2;
      const mk = (p, vv) => { const vol = o.vol(p), nn = mixv(cn, vol, o.volMix ?? 0.6), ao = o.ao(p); return G.vert(p, nn, cn, [u, vv], [o.col[0] * ao, o.col[1] * ao, o.col[2] * ao], [o.snow, 1, o.wind(p), ao]); };
      return [mk(row.c, vc), mk(pe, ve)];
    });
    for (let i = 0; i < segs; i++) {
      const [a, b] = ids[i], [c, e] = ids[i + 1];
      if (s === 0) { G.tri(a, b, c); G.tri(b, e, c); } else { G.tri(a, c, b); G.tri(b, c, e); }
    }
  }
}

/** Flat quad card (birch twig fans, spruce leader): centre-bottom at p0, up axis, right axis, size. */
function card(G, p0, up, right, w, h, reg, o) {
  const rr = uvRect(reg), n = norm(cross(right, up));
  const corners = [[-0.5, 0], [0.5, 0], [0.5, 1], [-0.5, 1]].map(([x, y]) => add(add(p0, right, x * w), up, y * h));
  const uvs = [[rr.u0, rr.v0], [rr.u1, rr.v0], [rr.u1, rr.v1], [rr.u0, rr.v1]];
  const ids = corners.map((p, i) => { const vol = o.vol(p), nn = mixv(n, vol, o.volMix ?? 0.7), ao = o.ao(p); return G.vert(p, nn, n, uvs[i], [o.col[0] * ao, o.col[1] * ao, o.col[2] * ao], [o.snow, 1, o.wind(p), ao]); });
  G.tri(ids[0], ids[1], ids[2]); G.tri(ids[0], ids[2], ids[3]);
}

/** Smooth ellipsoid "pad" (stylised): flattened bottom, noise on the rim. */
function pad(G, c, rx, ry, rz, o, R) {
  const W = 9, Hh = 5, base = G.n;
  for (let j = 0; j <= Hh; j++) {
    const ph = (j / Hh) * Math.PI, sy = Math.cos(ph), sr = Math.sin(ph);
    for (let k = 0; k <= W; k++) {
      const a = (k / W) * Math.PI * 2, jit = 1 + (k % W ? (R() - 0.5) * 0.18 : 0);
      const lx = Math.cos(a) * sr * rx * jit, lz = Math.sin(a) * sr * rz * jit, ly = sy * ry * (sy < 0 ? 0.45 : 1);
      const p = [c[0] + lx, c[1] + ly, c[2] + lz];
      const n = norm([lx / (rx * rx), ly / (ry * ry * (sy < 0 ? 0.45 : 1)), lz / (rz * rz)]);
      const rr = uvRect(REG.solid), ao = o.ao(p);
      G.vert(p, mixv(n, o.vol(p), 0.25), n, [lerp(rr.u0, rr.u1, k / W), lerp(rr.v0, rr.v1, j / Hh)], [o.col[0] * ao, o.col[1] * ao, o.col[2] * ao], [o.snow, 1, o.wind(p), ao]);
    }
  }
  for (let j = 0; j < Hh; j++) for (let k = 0; k < W; k++) { const a = base + j * (W + 1) + k, b = a + 1, cc = a + W + 1, d = cc + 1; G.tri(a, b, cc); G.tri(b, d, cc); }
}

// ------------------------------------------------------------------ species (metres, reference sizes)
// Shapes read from the client's drone photos (Drone Property 4/5/20/22/23): slender Lapland pines with a crown of
// irregular branch layers from about mid height, narrow drooping spruce spires from near the ground, small
// multi-stem leafless birches with a fine purple-grey twig haze.
const REF = { pine: { H: 12, R: 1.9 }, spruce: { H: 10, R: 1.55 }, birch: { H: 8, R: 2.0 } };
const COL = { pineN: [1, 1, 1], spruceN: [1, 1, 1], twig: [1, 1, 1], stylPine: hexRGB('#34472f'), stylSpruce: hexRGB('#25392c') };

function crownFns(H, R, yb, windH = H) {
  const vol = (p) => { const t = clamp01((p[1] - yb) / (H - yb)); const rad = Math.hypot(p[0], p[2]) / R; return norm([p[0], (0.25 + 1.4 * t * t) * R * (0.6 + rad), p[2]]); };
  const ao = (p) => { const t = clamp01((p[1] - yb) / (H - yb)); const rad = Math.hypot(p[0], p[2]) / (R * (0.35 + 0.65 * (1 - t))); return lerp(0.5, 1, smooth(0.05, 0.95, rad)) * lerp(0.82, 1, t); };
  const wind = (p) => { const y = p[1] / windH; return y * y; };
  return { vol, ao, wind };
}

/** A needle clump: a folded card (top, carries the snow) + a crossing upright card along the branch (side
 *  silhouette), so a clump has volume from every angle. */
function clump(G, p0, d, len, hw, o) {
  tent(G, p0, d, len, hw, THREE.MathUtils.degToRad(o.fold ?? 26), o.droop ?? 0.12, o.reg, o);
  // two cards rolled about the branch axis (a star in cross-section): volume from the side and from below
  const ax = norm(d), side = norm(cross([0, 1, 0], ax)), upv = norm(cross(ax, side));
  for (const roll of [-0.95, 0.95]) {
    const r = norm(add(add([0, 0, 0], upv, Math.cos(roll)), side, Math.sin(roll)));
    card(G, add(add(p0, ax, len * 0.04), r, -hw * 0.7), r, ax, len * 0.9, hw * 1.4, o.reg, { ...o, snow: (o.snow ?? 1) * 0.5 });
  }
}
// WS-34 hq (vegetation.trees.hq, preview-gated): `crown` adds detail to the same skeleton from a second random
// stream (R2), so the base tree keeps its shape: clumps (x per branch), gaps (x gap chance), tufts (side shoots on
// long branches, chance), top (x top clumps), lower (crown base lowered by this share of H). Absent = unchanged.
function cardsPine(seed, hq = {}) {
  const R = mulberry(seed), { H, R: CR } = REF.pine, G = new Geo();
  const C = hq.crown || {}, R2 = mulberry(seed ^ 0x2f6b);
  const yb = H * lerp(0.4, 0.48, R()) - H * (C.lower || 0);
  const { vol, ao, wind } = crownFns(H, CR, yb);
  // trunk: slight S-bend, grey-brown low, orange high (bark strip)
  const lean = [(R() - 0.5) * 0.25, (R() - 0.5) * 0.25];
  const tp = [], tr = [];
  for (let i = 0; i <= 6; i++) { const t = i / 6; tp.push([lean[0] * t * t + Math.sin(t * 5 + seed) * 0.05, t * H * 0.985, lean[1] * t * t + Math.cos(t * 4 + seed) * 0.05]); tr.push(lerp(0.17, 0.03, Math.pow(t, 0.8))); }
  tube(G, tp, tr, 7, REG.pineBark, H, { uRep: 1, ao: () => 1, wind, snow: 0.2 });
  const trunkAt = (y) => { const t = clamp01(y / H); return [lean[0] * t * t, y, lean[1] * t * t]; };
  // whorls: short branches carrying needle clumps along their outer two thirds (Lapland pine: narrow crown)
  let y = yb, wi = 0;
  while (y < H - 0.6) {
    const t = (y - yb) / (H - yb);
    const nb = 3 + Math.floor(R() * 2.6);
    const L0 = CR * Math.pow(1 - t, 0.5) * lerp(0.7, 1, smooth(0, 0.3, t));
    for (let b = 0; b < nb; b++) {
      if (R() < (0.1 + 0.12 * (1 - t)) * (C.gaps ?? 1)) continue; // gaps: Lapland pine crowns are patchy
      const az = wi * 2.39996 + b * (6.283 / nb) + (R() - 0.5) * 0.7;
      const L = L0 * lerp(0.62, 1.05, R());
      if (L < 0.2) continue;
      const el = THREE.MathUtils.degToRad(lerp(-6, 42, t * t) + (R() - 0.5) * 18);
      const d = [Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)];
      const s = trunkAt(y), e = add(s, d, L * 0.8), mid = add(add(s, d, L * 0.45), [0, 1, 0], L * 0.04);
      tube(G, [s, mid, e], [0.03 * L / CR + 0.016, 0.022, 0.012], 3, REG.pineBark, H, { vFixed: uvRect(REG.pineBark).v1 - 0.02, ao: (p) => ao(p) * 0.9, wind, snow: 0.3 });
      const nt = L > 1.0 ? 3 : 2, snowT = clamp01(0.65 + 0.35 * t);
      for (let k = 0; k < nt; k++) {
        const f = lerp(0.3, 0.62, k / Math.max(1, nt - 1)) + (R() - 0.5) * 0.06;
        const yaw = (R() - 0.5) * 0.7, dd = norm([Math.cos(az + yaw), d[1] * 0.6 + 0.12 + 0.2 * k / nt, Math.sin(az + yaw)]); // tips turn up
        const len = Math.min(L * 0.75, lerp(0.75, 1.05, R())) * (0.8 + 0.25 * k / nt), hw = len * lerp(0.36, 0.46, R());
        clump(G, add(s, d, L * f), dd, len, hw, { reg: REG.pine, vol, ao, wind, col: COL.pineN, snow: snowT * (k === nt - 1 ? 1 : 0.85), fold: lerp(20, 32, R()), droop: lerp(0.04, 0.16, R()) });
      }
      // WS-34 hq: extra clumps between the base ones and side tufts (a fuller, less plate-like silhouette)
      const ntX = Math.round(nt * ((C.clumps ?? 1) - 1));
      for (let k = 0; k < ntX; k++) {
        const f = lerp(0.22, 0.7, R2()), yaw = (R2() - 0.5) * 1.1, dd = norm([Math.cos(az + yaw), d[1] * 0.6 + 0.1 + 0.15 * R2(), Math.sin(az + yaw)]);
        const len = Math.min(L * 0.7, lerp(0.6, 0.9, R2())), hw = len * lerp(0.36, 0.46, R2());
        clump(G, add(s, d, L * f), dd, len, hw, { reg: REG.pine, vol, ao, wind, col: COL.pineN, snow: snowT * 0.9, fold: lerp(20, 32, R2()), droop: lerp(0.04, 0.14, R2()) });
      }
      if (C.tufts && L > 0.8 && R2() < C.tufts) for (const sd of [-1, 1]) {
        if (R2() < 0.35) continue;
        const a2 = az + sd * lerp(0.7, 1.1, R2()), dd = norm([Math.cos(a2), 0.25 + 0.2 * R2(), Math.sin(a2)]), len = lerp(0.45, 0.65, R2());
        clump(G, add(s, d, L * lerp(0.35, 0.55, R2())), dd, len, len * 0.4, { reg: REG.pine, vol, ao, wind, col: COL.pineN, snow: snowT * 0.8, fold: 24, droop: 0.1 });
      }
    }
    y += lerp(0.34, 0.5, R()); wi++;
  }
  // irregular rounded top: clumps pointing up and out around the leader
  const top = trunkAt(H * 0.96);
  for (let k = 0; k < 5; k++) {
    const az = k * 1.3 + R(), dd = norm([Math.cos(az) * 0.85, 0.45 + R() * 0.3, Math.sin(az) * 0.85]);
    clump(G, add(top, [0, -0.7 - R() * 0.3, 0]), dd, lerp(0.6, 0.85, R()), 0.3, { reg: REG.pine, vol, ao, wind, col: COL.pineN, snow: 1, fold: 26, droop: 0.06 });
  }
  for (let k = 0, n = Math.round(5 * ((C.top ?? 1) - 1)); k < n; k++) { // WS-34 hq: a rounder, denser top
    const az = k * 2.1 + R2() * 1.5, dd = norm([Math.cos(az) * 0.8, 0.35 + R2() * 0.4, Math.sin(az) * 0.8]);
    clump(G, add(top, [0, -1.1 - R2() * 0.9, 0]), dd, lerp(0.55, 0.8, R2()), 0.28, { reg: REG.pine, vol, ao, wind, col: COL.pineN, snow: 1, fold: 26, droop: 0.08 });
  }
  return { geo: G.build(H, CR), H, R: CR };
}

function cardsSpruce(seed, hq = {}) {
  const R = mulberry(seed), { H, R: CR } = REF.spruce, G = new Geo();
  const C = hq.spruce || {}, R2 = mulberry(seed ^ 0x51a7); // WS-34 hq: layers (x branches per whorl), droop (x tip droop)
  const yb = 0.25;
  const { vol, ao, wind } = crownFns(H, CR, yb);
  tube(G, [[0, 0, 0], [0, H * 0.5, 0], [0, H * 0.97, 0]], [0.14, 0.08, 0.02], 6, REG.spruceBark, H, { ao: () => 0.8, wind, snow: 0.1 });
  // dark core: hides see-through, the inside of a spruce is near black
  {
    const rr = uvRect(REG.core), sides = 7, rows = [[0.35, 0.5 * CR * 0.55], [H * 0.5, 0.32 * CR * 0.55], [H * 0.92, 0.05]];
    const base = G.n;
    for (const [yy, rad] of rows) for (let k = 0; k <= sides; k++) {
      const a = (k / sides) * 6.283, n = norm([Math.cos(a), 0.35, Math.sin(a)]);
      const p = [Math.cos(a) * rad, yy, Math.sin(a) * rad];
      G.vert(p, n, n, [lerp(rr.u0, rr.u1, k / sides), lerp(rr.v0, rr.v1, yy / H)], [0.6, 0.6, 0.6], [0.35, 1, wind(p), 0.5]);
    }
    for (let i = 0; i < 2; i++) for (let k = 0; k < sides; k++) { const a = base + i * (sides + 1) + k, b = a + 1, c = a + sides + 1, d = c + 1; G.tri(a, c, b); G.tri(b, c, d); }
  }
  let y = yb, wi = 0;
  while (y < H - 0.5) {
    const t = (y - yb) / (H - yb);
    const nb = 6 + Math.floor(R() * 2);
    const L0 = CR * Math.pow(1 - t, 0.92) + 0.12;
    for (let b = 0; b < nb; b++) {
      if (R() < 0.08) continue;
      const az = wi * 2.39996 + b * (6.283 / nb) + (R() - 0.5) * 0.5;
      const L = L0 * lerp(0.82, 1.12, R());
      const el = THREE.MathUtils.degToRad(lerp(-24, 8, t) + (R() - 0.5) * 8);
      const d = [Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)];
      const snow = clamp01(0.7 + 0.3 * t);
      tent(G, [Math.cos(az) * 0.08, y, Math.sin(az) * 0.08], d, L, L * lerp(0.34, 0.42, R()), THREE.MathUtils.degToRad(lerp(12, 22, R())), lerp(0.18, 0.32, 1 - t) * (C.droop ?? 1), REG.spruce, { vol, ao, wind, col: COL.spruceN, snow, segs: 2, shape: (q) => 1 - 0.55 * q });
    }
    for (let b = 0, nX = Math.round(nb * ((C.layers ?? 1) - 1)); b < nX; b++) { // WS-34 hq: in-between branches, a fuller spire
      const az = wi * 2.39996 + (b + 0.5) * (6.283 / Math.max(1, nX)) + (R2() - 0.5) * 0.5, yy = y + lerp(0.12, 0.24, R2());
      const L = (CR * Math.pow(1 - clamp01((yy - yb) / (H - yb)), 0.92) + 0.12) * lerp(0.7, 0.95, R2());
      const el = THREE.MathUtils.degToRad(lerp(-24, 8, t) + (R2() - 0.5) * 8), d = [Math.cos(az) * Math.cos(el), Math.sin(el), Math.sin(az) * Math.cos(el)];
      tent(G, [Math.cos(az) * 0.08, yy, Math.sin(az) * 0.08], d, L, L * lerp(0.34, 0.42, R2()), THREE.MathUtils.degToRad(lerp(12, 22, R2())), lerp(0.18, 0.32, 1 - t) * (C.droop ?? 1), REG.spruce, { vol, ao, wind, col: COL.spruceN, snow: clamp01(0.7 + 0.3 * t), segs: 2, shape: (q) => 1 - 0.55 * q });
    }
    y += lerp(0.36, 0.48, R()); wi++;
  }
  // leader spike
  for (const a of [0.3, 0.3 + Math.PI / 2]) card(G, [0, H - 0.75, 0], [0, 1, 0], [Math.cos(a), 0, Math.sin(a)], 0.36, 0.8, REG.spruce, { vol, ao, wind, col: COL.spruceN, snow: 0.7, volMix: 0.85 });
  return { geo: G.build(H, CR), H, R: CR };
}

// WS-76 birch round 3 (vegetation.trees.hq.birch, preview-gated; each key absent = today's birch): stems (a fixed stem
// count instead of 1-3), forms (one per variant k: crown = limb length x, base = crown base share of H, lean, limbs =
// limb count x), fans 'fine' (the atlas's fine twig fans, and a lighter crown inside: no dark blobs), foot (atlas only).
function cardsBirch(seed, hq = {}, sparse = false, k0 = 0) {
  const R = mulberry(seed), { H, R: CR } = REF.birch, G = new Geo();
  const C = hq.birch || {}, R2 = mulberry(seed ^ 0x6b1c); // WS-34 hq: twigs (x twig fans), tint (twig colour multiplier)
  const twigCol = C.tint ? COL.twig.map((c, i) => c * C.tint[i]) : COL.twig;
  const F = C.forms?.length ? C.forms[k0 % C.forms.length] : null;
  const yb = H * (F?.base ?? 0.35);
  const { vol, ao: ao0, wind } = crownFns(H, CR, yb);
  const ao = C.fans === 'fine' ? (p) => 0.4 + 0.6 * ao0(p) : ao0;
  const stems0 = 1 + (R() < 0.45 ? 1 : 0) + (R() < 0.15 ? 1 : 0), stems = C.stems ?? stems0;
  for (let s = 0; s < stems; s++) {
    const az = s * 2.4 + R(), lean0 = stems > 1 ? lerp(0.05, 0.14, R()) : lerp(0, 0.05, R()), lean = F?.lean ?? lean0;
    const sh = H * (s === 0 ? 1 : lerp(0.75, 0.92, R()));
    const pts = [], rad = [];
    for (let i = 0; i <= 5; i++) { const t = i / 5; pts.push([Math.cos(az) * (0.12 * (s > 0 ? 1 : 0) + lean * t * sh) + Math.sin(t * 7 + seed) * 0.04, t * sh, Math.sin(az) * (0.12 * (s > 0 ? 1 : 0) + lean * t * sh)]); rad.push(lerp(s === 0 ? 0.11 : 0.08, 0.015, Math.pow(t, 0.75))); }
    tube(G, pts, rad, 6, REG.birchBark, H, { ao: () => 1, wind, snow: 0.3 });
    const nl0 = sparse ? 4 : 6 + Math.floor(R() * 3), nl = F ? Math.round(nl0 * (F.limbs ?? 1)) : nl0, tl0 = F ? (F.base ?? 0.35) + 0.04 : 0.4;
    for (let k = 0; k < nl; k++) {
      const t = lerp(tl0, 0.9, k / nl) + (R() - 0.5) * 0.05, i = Math.min(4, Math.floor(t * 5)), f = t * 5 - i;
      const s0 = add(pts[i], [pts[i + 1][0] - pts[i][0], pts[i + 1][1] - pts[i][1], pts[i + 1][2] - pts[i][2]], f);
      const a = k * 2.2 + az + (R() - 0.5), el = THREE.MathUtils.degToRad(lerp(38, 62, R()));
      const L = CR * lerp(0.45, 0.95, R()) * (1 - 0.45 * t) * (F?.crown ?? 1);
      const d = [Math.cos(a) * Math.cos(el), Math.sin(el), Math.sin(a) * Math.cos(el)];
      const e = add(add(s0, d, L), [0, 1, 0], L * 0.15), m = add(s0, d, L * 0.5);
      tube(G, [s0, m, e], [0.03, 0.018, 0.008], 3, REG.birchBark, H, { vFixed: uvRect(REG.birchBark).v1 - 0.05, ao: () => 0.85, wind, snow: 0.3 });
      // twig fans rising from the limb's outer half (crossed pairs), fine and sparse: the crown is a haze
      const nc = sparse ? 1 : 2;
      for (let c = 0; c < nc; c++) {
        const p0 = add(s0, d, L * lerp(0.45, 0.9, (c + R() * 0.5) / nc));
        const sz = lerp(0.9, 1.4, R()) * (1 - 0.3 * t), right = norm([Math.cos(a + 1.57), 0, Math.sin(a + 1.57)]);
        const up = norm([d[0] * 0.5, 1, d[2] * 0.5]);
        card(G, add(p0, up, -sz * 0.15), up, right, sz * 0.95, sz, REG.birch, { vol, ao, wind, col: twigCol, snow: 0.55 });
        card(G, add(p0, up, -sz * 0.15), up, norm(cross(up, right)), sz * 0.75, sz * 0.9, REG.birch, { vol, ao, wind, col: twigCol, snow: 0.55 });
      }
      for (let c = 0, nX = Math.round(nc * ((C.twigs ?? 1) - 1)); c < nX; c++) { // WS-34 hq: a finer, fuller twig haze
        const p0 = add(s0, d, L * lerp(0.3, 0.95, R2())), sz = lerp(0.7, 1.15, R2()) * (1 - 0.3 * t), a2 = a + (R2() - 0.5) * 1.4;
        const right = norm([Math.cos(a2 + 1.57), 0, Math.sin(a2 + 1.57)]), up = norm([d[0] * 0.6, 1, d[2] * 0.6]);
        card(G, add(p0, up, -sz * 0.15), up, right, sz * 0.9, sz, REG.birch, { vol, ao, wind, col: twigCol, snow: 0.55 });
      }
    }
    // crown top
    const top = pts[5];
    card(G, add(top, [0, -0.6, 0]), [0, 1, 0], [Math.cos(az), 0, Math.sin(az)], 1.1, 1.2, REG.birch, { vol, ao, wind, col: twigCol, snow: 0.55 });
    card(G, add(top, [0, -0.6, 0]), [0, 1, 0], [-Math.sin(az), 0, Math.cos(az)], 0.9, 1.1, REG.birch, { vol, ao, wind, col: twigCol, snow: 0.55 });
  }
  return { geo: G.build(H, CR), H, R: CR };
}

// --- direction B: refined stylised (clean, light, smooth-shaded; no needle cards)
function stylPine(seed) {
  const R = mulberry(seed + 100), { H, R: CR } = REF.pine, G = new Geo();
  const yb = H * lerp(0.42, 0.5, R());
  const { vol, ao, wind } = crownFns(H, CR, yb);
  const tp = [], tr = [];
  for (let i = 0; i <= 4; i++) { const t = i / 4; tp.push([Math.sin(t * 4 + seed) * 0.05, t * H * 0.96, Math.cos(t * 3 + seed) * 0.05]); tr.push(lerp(0.16, 0.035, t)); }
  tube(G, tp, tr, 6, REG.pineBark, H, { ao: () => 1, wind, snow: 0.2 });
  const n = 5 + Math.floor(R() * 2);
  for (let k = 0; k < n; k++) {
    const t = k / (n - 1), y = lerp(yb + 0.4, H - 0.6, t);
    const r = CR * lerp(0.95, 0.45, t) * lerp(0.8, 1.05, R());
    const az = k * 2.4 + R(), off = r * 0.35 * (1 - t);
    pad(G, [Math.cos(az) * off, y, Math.sin(az) * off], r, lerp(0.6, 0.85, R()), r * lerp(0.75, 0.95, R()), { vol, ao, wind, col: COL.stylPine, snow: clamp01(0.7 + 0.3 * t) }, R);
  }
  return { geo: G.build(H, CR), H, R: CR };
}
function stylSpruce(seed) {
  const R = mulberry(seed + 200), { H, R: CR } = REF.spruce, G = new Geo();
  const { vol, ao, wind } = crownFns(H, CR, 0.3);
  tube(G, [[0, 0, 0], [0, 0.6, 0]], [0.13, 0.11], 6, REG.spruceBark, H, { ao: () => 0.8, wind, snow: 0 });
  const tiers = 7, rr = uvRect(REG.solid);
  for (let k = 0; k < tiers; k++) {
    const t = k / tiers, y0 = lerp(0.3, H * 0.9, t), y1 = y0 + H * 0.24 * (1 - t * 0.35), r = CR * Math.pow(1 - t, 0.85) * lerp(0.92, 1.06, R()) + 0.1;
    const W = 12, base = G.n;
    const ring = [];
    for (let i = 0; i <= W; i++) {
      const a = (i / W) * 6.283 + k * 0.4, sc = i % 2 ? 0.86 : 1, dr = i % 2 ? 0 : 0.12 * r;
      ring.push([Math.cos(a) * r * sc, y0 - dr, Math.sin(a) * r * sc]);
    }
    const apex = [0, y1, 0];
    const tipN = norm([0, 1, 0]);
    for (let i = 0; i <= W; i++) {
      const p = ring[i], n = norm([p[0], r * 0.9, p[2]]), a2 = ao(p);
      G.vert(p, mixv(n, vol(p), 0.2), n, [lerp(rr.u0, rr.u1, i / W), rr.v0 + 0.01], [a2 * COL.stylSpruce[0], a2 * COL.stylSpruce[1], a2 * COL.stylSpruce[2]], [clamp01(0.75 + 0.25 * t), 1, wind(p), a2]);
      G.vert(apex, tipN, tipN, [lerp(rr.u0, rr.u1, i / W), rr.v1 - 0.01], COL.stylSpruce.map((c) => c * 1.2), [1, 1, wind(apex), 1]);
    }
    for (let i = 0; i < W; i++) { const a = base + i * 2, b = a + 2; G.tri(a, a + 1, b); G.tri(b, a + 1, b + 1); }
    // underside (dark), so the skirt reads solid from low angles
    const ub = G.n;
    for (let i = 0; i <= W; i++) { const p = ring[i]; G.vert(p, [0, -1, 0], [0, -1, 0], [rr.u0, rr.v0], COL.stylSpruce.map((c) => c * 0.35), [0, 1, 0, 0.35]); }
    const cIdx = G.vert([0, y0 + 0.15, 0], [0, -1, 0], [0, -1, 0], [rr.u0, rr.v0], COL.stylSpruce.map((c) => c * 0.3), [0, 1, 0, 0.3]);
    for (let i = 0; i < W; i++) G.tri(ub + i, cIdx, ub + i + 1);
  }
  return { geo: G.build(H, CR), H, R: CR };
}
function stylBirch(seed) { return cardsBirch(seed + 300, {}, true); }

const DIRECTIONS = {
  cards: { label: 'Realistic cards', make: [cardsPine, cardsSpruce, cardsBirch] },
  stylised: { label: 'Refined stylised', make: [stylPine, stylSpruce, stylBirch] },
};
const SPECIES = ['pine', 'spruce', 'birch'];
const SPECIES_COL = { pine: '#ffffff', spruce: '#ffffff', birch: '#ffffff' };

// ------------------------------------------------------------------ 3. node materials (WS-75: TSL)
// Every uniform is a named uniform node (`.value`): main.js, Fine-tune and the kit trees drive them.
export const vegCap = (acc, up, cap) => acc.mul(smoothstep(0.08, 0.75, up)).mul(cap);
/** Mip level of a texture coordinate in texels (screen-space derivatives). */
export const vegMip = (uvPx) => { const dx = dFdx(uvPx), dy = dFdy(uvPx); return max(log2(max(max(dot(dx, dx), dot(dy, dy)), 1e-8)).mul(0.5), 0); };
const LUMA = vec3(0.2126, 0.7152, 0.0722);
const one = float(1);
/** The instance tint: a sharedInstances() mesh's iC attribute, an InstancedMesh's instanceColor, white for a mesh without one. */
export const instTint = Fn((builder) => (builder.geometry?.attributes?.iC ? attribute('iC', 'vec3') : builder.object?.instanceColor ? instanceColor : vec3(1)), 'vec3')();

// WS-76 birch leaves (vegetation.trees.hq.birch: leaves, summer / autumn palettes; preview-gated). S.uLeafHq = (cut, shade,
// palette size, variants per species); all 0 = today's leaves exactly. The season's leaf mask: with a cut the atlas holds
// cluster ranks, so a thinner season (foliage < 1) drops whole clusters, the lowest ranks first.
const leafMask = (S, A4, mip) => {
  const thr = one.sub(S.uSeason.x.mul(one.sub(S.uLeafHq.x))); // cut 0: 1 - foliage, as before
  return select(S.uSeason.x.greaterThan(0.001), smoothstep(thr.sub(0.04), thr.add(0.04), A4.g.mul(mip.mul(0.34).add(1))).mul(step(0.004, A4.g)), 0);
};
const leafShade = (S, A4) => A4.b.mul(0.45).add(0.7).mul(mix(one, A4.a.mul(0.6).add(0.5), S.uLeafHq.y)); // aux A: the cluster's shade
// the tree's leaf colour from the season's palette (one per tree, picked by its seed); no palette = the season's colour
const leafPick = (U, seed) => {
  const n = U.uLeafHq.z, i = int(min(floor(fract(seed.mul(91.7).add(0.13)).mul(n)), max(n.sub(1), 0)));
  return select(n.greaterThan(0.5), U.uLeafPal.element(i).xyz, U.uLeafCol);
};
/** Trees between the camera and the selected place fade out (uFocus.w = radius, 0 = off). Vertex stage. */
export function vegFocus(U, base, h, r) {
  const seg = U.uFocus.xyz.sub(U.uCam), L2 = max(dot(seg, seg), 1e-4);
  const t = dot(base.add(vec3(0, h.mul(0.5), 0)).sub(U.uCam), seg).div(L2);
  const pl = U.uCam.add(seg.mul(t));
  const d = distance(pl, vec3(base.x, clamp(pl.y, base.y, base.y.add(h)), base.z));
  const occ = one.sub(smoothstep(U.uFocus.w, U.uFocus.w.mul(1.5).add(r), d));
  return select(U.uFocus.w.lessThanEqual(0).or(t.lessThanEqual(0.02)).or(t.greaterThanEqual(1)), one, one.sub(occ)); // WS-19 rc1: fully, no hint
}
/** WS-18 r2 aerial perspective: far trees take on a desaturated horizon colour (start m, scale m, max share) when the
 *  scene has fog; after the fog, before tone mapping. k scales it (the landscape's canopy share). */
export function hazeOver(out, U, k = null) {
  const hd = length(positionView);
  let hk = U.uHaze.z.mul(one.sub(exp(max(hd.sub(U.uHaze.x), 0).negate().div(max(U.uHaze.y, 1)))));
  if (k) hk = hk.mul(k);
  const fc = U.uFogCol, hc = mix(fc, vec3(dot(fc, LUMA)), 0.55);
  return vec4(mix(out.rgb, hc, clamp(hk.mul(U.uFogOn), 0, 1)), out.a);
}

// Lighting scales on the summed light terms: k = { dd, ds, id } multiply direct diffuse, direct specular and indirect
// diffuse. The direct specular lobe uses the material's specularF90 (three's node BRDF fixes it at 1: a needle sheen of
// F90 0.15 turned into a bright rim at grazing sun angles, the needles read tan).
class VegLighting extends THREE.PhysicalLightingModel {
  constructor(k = {}) { super(); this.k = k; }
  direct({ lightDirection, lightColor, reflectedLight }) {
    const irradiance = normalView.dot(lightDirection).clamp().mul(lightColor).toVar();
    const dotVH = positionViewDirection.dot(lightDirection.add(positionViewDirection).normalize()).clamp();
    const F = F_Schlick({ f0: specularColor, f90: specularF90, dotVH });
    reflectedLight.directDiffuse.addAssign(irradiance.mul(BRDF_Lambert({ diffuseColor: diffuseContribution })).mul(F.oneMinus()));
    reflectedLight.directSpecular.addAssign(irradiance.mul(BRDF_GGX({ lightDirection, f0: specularColorBlended, f90: specularF90, roughness })).mul(this.multiScatteringCompensation));
  }
  start(builder) {
    super.start(builder);
    const r = builder.context.reflectedLight, k = this.k;
    if (k.dd) r.directDiffuse.mulAssign(k.dd);
    if (k.ds) r.directSpecular.mulAssign(k.ds);
    if (k.id) r.indirectDiffuse.mulAssign(k.id);
  }
}
/**
 * The vegetation's standard material: veg = { spec: 1 (needle sheen: F90 0.15, specular x 0.35) | 0, light: { dd, ds, id } (VegLighting), haze: U (hazeOver), ownColor (the
 * colorNode already holds the vertex and instance colours: snow is mixed in after them), depth (a depth node, main pass
 * only: the shadow pass keeps the caster's own depth; no texture reads on RENDER TARGETS in it, see debug), debug: (out) =>
 * out (no texture reads: on the WebGL2 backend a render-target read in the output stage has no statement stack) }.
 */
export class VegNodeMaterial extends THREE.MeshStandardNodeMaterial {
  constructor(params, veg = {}) { super(params); this.veg = veg; }
  customProgramCacheKey() { const v = this.veg; return `${super.customProgramCacheKey()}|veg${v.spec ?? 1}${v.light ? 'L' : ''}${v.haze ? 'H' : ''}${v.ownColor ? 'C' : ''}${v.depth ? 'D' : ''}${v.debug ? 'Q' : ''}`; }
  setupSpecular() {
    super.setupSpecular();
    const s = this.veg.spec ?? 1;
    if (s) { specularColor.mulAssign(0.35); specularColorBlended.mulAssign(0.35); specularF90.assign(0.15); }
  }
  setupLightingModel(builder) { return this.veg.light || (this.veg.spec ?? 1) ? new VegLighting(this.veg.light) : super.setupLightingModel(builder); }
  setupDiffuseColor(builder) {
    if (!this.veg.ownColor) return super.setupDiffuseColor(builder);
    if (this.maskNode !== null) bool(this.maskNode).not().discard();
    diffuseColor.assign(vec4(this.colorNode));
    if (this.alphaTest > 0) {
      if (this.alphaToCoverage) {
        diffuseColor.a.assign(smoothstep(materialAlphaTest, materialAlphaTest.add(fwidth(diffuseColor.a)), diffuseColor.a));
        diffuseColor.a.lessThanEqual(0).discard();
      } else diffuseColor.a.lessThanEqual(materialAlphaTest).discard();
    }
    if (builder.isOpaque()) diffuseColor.a.assign(1);
  }
  setupDepth(builder) { if (this.veg.depth) { depth.assign(this.veg.depth).toStack(); return; } super.setupDepth(builder); }
  setupOutput(builder, out) {
    let o = super.setupOutput(builder, out);
    if (this.veg.haze) o = hazeOver(o, this.veg.haze);
    if (this.veg.debug) o = this.veg.debug(o);
    return o;
  }
}

// Near 3D model (no wind sway: owner 2026-10-27, too heavy to process): the dithered hand-over (vHiW, complementary to the
// impostors' and in the shadow pass too), density by rank, the focus fade, snow by load against the per-texel capacity,
// seasons (leaf mask, needle tint). Instance matrices are yaw + scale (r, h, r): their z axis is the x axis turned.
function hiMaterial(U, atlas, { a2c }) {
  const aTree = attribute('aTree', 'vec4'), aVeg = attribute('aVeg', 'vec4'), aCardN = attribute('aCardN', 'vec3');
  const V = { hiW: varyingProperty('float', 'vgHiW'), focus: varyingProperty('float', 'vgFocus'), seed: varyingProperty('float', 'vgSeed'), up: varyingProperty('float', 'vgCardUp'), leafC: varyingProperty('vec3', 'vgLeafC') };
  const pos = (depthPass) => Fn(() => {
    const c0 = iCol0.xyz, c1 = iCol1.xyz, tB = iCol3.xyz, c2 = vec3(c0.z.negate(), 0, c0.x);
    const tR = length(c0), tH = length(c1);
    const p = positionGeometry.toVar();
    const tD = distance(U.uCam, tB.add(vec3(0, tH.mul(0.5), 0)));
    const hiW = select(U.uLod.z.greaterThan(0.5), one.sub(smoothstep(U.uLod.x.sub(U.uLod.y), U.uLod.x, tD)), one).toVar();
    V.hiW.assign(hiW); V.seed.assign(aTree.z); V.focus.assign(depthPass ? one : vegFocus(U, tB, tH, tR));
    V.leafC.assign(leafPick(U, aTree.z)); // WS-76: this tree's leaf colour
    p.assign(select(aTree.y.greaterThanEqual(U.uDensity).or(hiW.lessThanEqual(0)), vec3(0), p));
    const r2 = dot(c0, c0), h2 = dot(c1, c1), cn = aCardN.div(vec3(r2, h2, r2)); // the card normal through the instance (inverse transpose)
    V.up.assign(normalize(c0.mul(cn.x).add(c1.mul(cn.y)).add(c2.mul(cn.z)).add(vec3(0, 1e-6, 0))).y);
    if (!depthPass) { const nl = normalLocal.div(vec3(r2, h2, r2)); normalLocal.assign(normalize(c0.mul(nl.x).add(c1.mul(nl.y)).add(c2.mul(nl.z)))); } // the vertex normal through the instance too (a sharedInstances() mesh: three applies no instancing)
    return c0.mul(p.x).add(c1.mul(p.y)).add(c2.mul(p.z)).add(tB);
  })();
  const uvN = uv(), t = texture(atlas.map, uvN), A4 = texture(atlas.aux, uvN), mip = vegMip(uvN.mul(AT));
  // WS-42: the leaf mask thins with the mips like the twig alpha: boosted the same way, so birches keep their leaves from afar
  const leaf = leafMask(U, A4, mip); // WS-76: leafMask / leafShade / the per-tree colour (no hq.birch leaves = as before)
  let col = t.rgb.mul(mix(vec3(1), U.uNeedleTint, step(0.5, aVeg.y)));
  col = mix(col, V.leafC.mul(leafShade(U, A4)), leaf).mul(attribute('color', 'vec3')).mul(instTint);
  const up = V.up.mul(faceDirection);
  const thr = one.sub(vegCap(A4.r, up, aVeg.x));
  const sw = mip.mul(0.05).add(0.05).mul(U.uSnowHq.x); // a soft share of snow when the mask is minified, not a hard cut
  const S0 = select(A4.r.greaterThan(0.004).and(U.uSnow.x.greaterThan(0.001)), smoothstep(thr.sub(sw), thr.add(sw), U.uSnow.x), 0); // WS-35: load 0 = no snow
  const S = max(S0, U.uSnowHq.y.mul(A4.r).mul(smoothstep(0.15, 0.85, up)).mul(min(U.uSnow.x.mul(4), 1))); // WS-34 hq: frost dusting
  col = mix(col, U.uSnowCol.mul(aVeg.w.mul(A4.b).mul(0.2).add(0.8)), S);
  const a = max(max(t.a, leaf), S.mul(step(mip, 2))).mul(mip.mul(0.34).add(1)); // snow lumps only close up
  const q = fract(ignFrag().add(V.seed));
  const n0 = select(aVeg.y.greaterThan(0.5), normalViewGeometry, normalViewGeometry.mul(faceDirection)); // foliage: the crown-volume normal is never flipped
  const m = new VegNodeMaterial({ roughness: 0.92, metalness: 0, side: THREE.DoubleSide, alphaTest: 0.5, alphaToCoverage: a2c }, { ownColor: true, haze: U });
  m.positionNode = pos(false); m.castShadowPositionNode = pos(true);
  m.colorNode = vec4(col, a);
  const hg = heroGone(U, attribute('aTree', 'vec4').x); // VIS-176: the kit trees' take-over cross-fade
  m.maskNode = q.lessThan(V.hiW.mul(V.focus)).and(hg.gone.not());
  m.maskShadowNode = q.lessThan(V.hiW).and(hg.goneShadow.not());
  m.normalNode = normalize(mix(n0, normalize(cameraViewMatrix.mul(vec4(0, 1, 0, 0)).xyz), S.mul(0.55)));
  m.name = 'ws18-hi'; m.userData.lcFoliage = true; // lazy/lightcast.js: cast light with the foliage response
  return m;
}

// Impostor: billboard in the tree's normalised space, frame selection, per-frame reprojected UVs (vertex); the 3 nearest
// frames blended, coverage as alpha-to-coverage on MSAA tiers or a dither, the hand-over's other half.
function frameUV(U, g, P, vn, Rf) {
  const D = hemiDec(g.div(U.uImp.x.sub(1)).mul(2).sub(1)).toVar();
  const r0 = vcross(vec3(0, 1, 0), D), l = length(r0), r = select(l.greaterThan(1e-4), r0.div(l), vec3(1, 0, 0)).toVar();
  const u = vcross(D, r), dn0 = dot(vn, D), dn = select(dn0.greaterThanEqual(0), max(dn0, 0.05), min(dn0, -0.05));
  const Pp = P.sub(vn.mul(dot(P, D).div(dn)));
  return vec2(dot(Pp, r), dot(Pp, u)).div(Rf).mul(0.5).add(0.5);
}
/** The 3 frames of a view direction on an N x N hemi-octahedral grid: { g0, gB, g1, w }. */
export function frameTriangle(vs, N) {
  const Nm = N.sub(1), G = hemiEnc(vs).mul(0.5).add(0.5).mul(Nm).toVar();
  const g0 = clamp(floor(G), vec2(0), vec2(Nm.sub(1))).toVar(), f = clamp(G.sub(g0), 0, 1).toVar(), xy = f.x.greaterThanEqual(f.y);
  return { g0, gB: select(xy, g0.add(vec2(1, 0)), g0.add(vec2(0, 1))), g1: g0.add(1), w: select(xy, vec3(one.sub(f.x), f.x.sub(f.y), f.y), vec3(one.sub(f.y), f.y.sub(f.x), f.x)) };
}
/** Billboard basis facing vn: { bR, bU }. */
export function billboard(vn) {
  const b0 = vcross(vec3(0, 1, 0), vn), bl = length(b0), bR = select(bl.greaterThan(1e-4), b0.div(bl), vec3(1, 0, 0)).toVar();
  return { bR, bU: vcross(vn, bR) };
}
function impostorMaterial(U, tex) {
  const aTree = attribute('aTree', 'vec4');
  const V = { F0: varyingProperty('vec4', 'vgF0'), F1: varyingProperty('vec4', 'vgF1'), F2: varyingProperty('vec4', 'vgF2'), W: varyingProperty('vec3', 'vgW'),
    vi: varyingProperty('float', 'vgVar'), ex: varyingProperty('vec2', 'vgEx'), hiW: varyingProperty('float', 'vgHiW'), focus: varyingProperty('float', 'vgFocus'), seed: varyingProperty('float', 'vgSeed'),
    leafK: varyingProperty('vec3', 'vgLeafK') };
  const pos = (depthPass) => Fn(() => {
    const c0 = iCol0.xyz, tB = iCol3.xyz;
    const tR = max(length(c0), 0.05), tH = max(length(iCol1.xyz), 0.1);
    const ex = c0.div(tR), ez = vec3(c0.z.negate(), 0, c0.x).div(tR);
    const tC = tB.add(vec3(0, tH.mul(0.5), 0));
    const Rf = U.uImpRf.element(int(aTree.x.add(0.5)));
    const vw = cameraPosition.sub(tC).toVar();
    const vn = normalize(vec3(dot(vw, ex).div(tR), vw.y.div(tH.mul(0.5)), dot(vw, ez).div(tR)).add(vec3(0, 1e-6, 0))).toVar();
    const { bR, bU } = billboard(vn);
    const P = bR.mul(positionGeometry.x).add(bU.mul(positionGeometry.y)).mul(Rf).toVar();
    const T = frameTriangle(normalize(vec3(vn.x, max(vn.y, 0).add(1e-4), vn.z)), U.uImp.x);
    V.F0.assign(vec4(frameUV(U, T.g0, P, vn, Rf), T.g0)); V.F1.assign(vec4(frameUV(U, T.gB, P, vn, Rf), T.gB)); V.F2.assign(vec4(frameUV(U, T.g1, P, vn, Rf), T.g1));
    V.W.assign(T.w); V.vi.assign(aTree.x); V.seed.assign(aTree.z); V.ex.assign(vec2(ex.x, ex.z));
    // WS-76: the bake holds the palette's mean leaf colour; a birch takes its own colour as a ratio on its leafy pixels
    const sp = floor(aTree.x.add(0.5).div(max(U.uLeafHq.w, 1))), isB = U.uLeafHq.z.greaterThan(0.5).and(sp.equal(float(SPECIES.indexOf('birch'))));
    V.leafK.assign(select(isB, clamp(leafPick(U, aTree.z).div(max(U.uLeafCol, vec3(1e-3))), 0.3, 3), vec3(1)));
    const wpos = tC.add(ex.mul(P.x.mul(tR))).add(vec3(0, P.y.mul(tH).mul(0.5), 0)).add(ez.mul(P.z.mul(tR))).toVar();
    if (depthPass) wpos.subAssign(normalize(vw.add(vec3(0, 1e-5, 0))).mul(tR).mul(U.uImp.w));
    const tD = distance(U.uCam, tC);
    const hiW = select(U.uLod.z.greaterThan(0.5), one.sub(smoothstep(U.uLod.x.sub(U.uLod.y), U.uLod.x, tD)), 0).toVar();
    V.hiW.assign(hiW); V.focus.assign(depthPass ? one : vegFocus(U, tB, tH, tR));
    const thinK = one.sub(U.uThin.x.mul(smoothstep(U.uThin.y, U.uThin.z, tD))); // r2: the far forest thins a little (no solid wall)
    return select(aTree.y.greaterThanEqual(U.uDensity.mul(thinK)).or(hiW.greaterThanEqual(1)), tB, wpos);
  })();
  const atlasUV = (F) => {
    const cols = U.uImpAtlas.x, rows = U.uImpAtlas.y, v = floor(V.vi.add(0.5));
    const q0 = vec2(mod(v, cols), floor(v.div(cols))).add(floor(F.zw.add(0.5)).add(clamp(F.xy, 0, 1)).div(U.uImp.x)).div(vec2(cols, rows));
    return vec2(q0.x, one.sub(q0.y)); // WS-75: a render target is read top row first (clip y +1 = v 0) on both backends
  };
  const tap = (F, w) => { // both atlases premultiplied by coverage (r2 bake); texture reads outside any branch
    const k = select(F.x.greaterThanEqual(0).and(F.y.greaterThanEqual(0)).and(F.x.lessThanEqual(1)).and(F.y.lessThanEqual(1)).and(w.greaterThan(0)), w, 0);
    const uvq = atlasUV(F);
    return [tex.a(uvq).mul(k), tex.n(uvq).mul(k)];
  };
  const s = [tap(V.F0, V.W.x), tap(V.F1, V.W.y), tap(V.F2, V.W.z)];
  const acc = s[0][0].add(s[1][0]).add(s[2][0]), nrm = s[0][1].add(s[1][1]).add(s[2][1]);
  const iRaw = acc.rgb.div(max(acc.a, 1e-4)), iMx = max(iRaw.r, max(iRaw.g, iRaw.b));
  const iCol = iRaw.mul(mix(vec3(1), V.leafK, smoothstep(0.3, 0.7, iMx.sub(min(iRaw.r, min(iRaw.g, iRaw.b))).div(max(iMx, 1e-3))))); // WS-76: leafy (saturated) pixels only, not the white stems
  const iA = pow(clamp(acc.a, 0, 1), U.uImp.z); // stored ~ coverage x alpha (bake); exponent fitted against the 3D model (QA impmatch)
  const iCov = max(nrm.w, 1e-3);
  const iN = octDecY(clamp(nrm.xy.div(iCov), 0, 1).mul(2).sub(1));
  const iS = select(U.uSnow.x.greaterThan(0.001), clamp(nrm.z.div(iCov), 0, 1), 0); // baked snow share at the current load
  const q = fract(ignFrag().add(V.seed));
  const d2 = fract(ign(screenCoordinate.yx.mul(1.37).add(17)).add(V.seed.mul(0.61)));
  const keepA = (A) => U.uA2C.greaterThan(0.5).or(A.greaterThanEqual(d2)).and(A.greaterThanEqual(0.02)); // a dither where there is no MSAA
  const wm = max(V.W.x, max(V.W.y, V.W.z)), [accS] = tap(select(V.W.x.greaterThanEqual(wm), V.F0, select(V.W.y.greaterThanEqual(wm), V.F1, V.F2)), one); // the shadow: the nearest frame only
  const iAS = pow(clamp(accS.a, 0, 1), U.uImp.z);
  const iEz = vec3(V.ex.y.negate(), 0, V.ex.x);
  const iNW = normalize(mix(normalize(vec3(V.ex.x, 0, V.ex.y).mul(iN.x).add(vec3(0, iN.y, 0)).add(iEz.mul(iN.z))), vec3(0, 1, 0), iS.mul(0.55)));
  // r2: the 3D crown shades most of its own needles (shadow map), the impostor quad cannot: its direct light is scaled by
  // the crown's average self-shadow so both match in colour
  const m = new VegNodeMaterial({ roughness: 0.9, metalness: 0, side: THREE.DoubleSide }, { light: { dd: U.uImpSun, ds: U.uImpSun }, haze: U });
  m.positionNode = pos(false); m.castShadowPositionNode = pos(true);
  // QA views (uImpDbg y / z / w: baked albedo / normal / snow share), shown as emission on a black albedo: built in the
  // colour and light stages (a texture read in the output stage has no statement stack on the WebGL2 backend)
  const dbgOn = U.uImpDbg.y.add(U.uImpDbg.z).add(U.uImpDbg.w).greaterThan(0.5);
  m.emissiveNode = select(dbgOn, select(U.uImpDbg.y.greaterThan(0.5), iCol, select(U.uImpDbg.z.greaterThan(0.5), iN.mul(0.5).add(0.5), vec3(iS, iS, iS))), vec3(0));
  // WS-75: alpha-to-coverage has 4 hard steps (4x MSAA) on WebGPU, with no dither between them: a far
  // crown's low coverage then rounded down to no samples (the forest read thin and washed out from the home view). A
  // per-pixel offset of +- half a step restores the mean coverage over neighbouring pixels.
  const a2cDither = fract(ign(screenCoordinate.xy.add(31.7)).add(V.seed.mul(0.37))).sub(0.5).mul(U.uA2CStep);
  // WS-75 perf: the cells are gpu/instance.js sharedInstances() (one node build for all): the per-tree tint is attribute iC
  m.colorNode = vec4(select(dbgOn, vec3(0), mix(iCol, U.uSnowCol.mul(0.92), iS)).mul(attribute('iC', 'vec3')), select(U.uA2C.greaterThan(0.5), clamp(iA.add(a2cDither), 0, 1), one));
  const hg = heroGone(U, V.vi); // VIS-176: the kit trees' take-over cross-fade
  m.maskNode = q.greaterThanEqual(V.hiW).and(q.lessThan(V.hiW.add(one.sub(V.hiW).mul(V.focus)))).and(keepA(iA)).and(hg.gone.not());
  m.maskShadowNode = q.greaterThanEqual(V.hiW).and(keepA(iAS)).and(iAS.greaterThanEqual(0.45)).and(hg.goneShadow.not()); // the shadow reads the nearest frame only
  m.normalNode = normalize(cameraViewMatrix.mul(vec4(select(U.uImpDbg.x.greaterThan(0.5), vec3(0, 1, 0), iNW), 0)).xyz); // QA dbg.x: force an up normal
  m.name = 'ws18-imp'; m.userData.lcFoliage = true; // lazy/lightcast.js: cast light with the foliage response
  return m;
}

// Bake material (WS-18 r2): the near model rendered unlit into the impostor atlases, ALL views of a variant in one
// instanced draw (instance = view: its basis aR/aU/aD and its cell in the atlas), into a 4x MSAA target with
// alpha-to-coverage, so a view stores the tree's true fractional coverage (a crown you can see through), not a
// solid cut-out. Every output is premultiplied by coverage (clear = 0) so the MSAA resolve and the mips average
// correctly: pass 0 = (albedo, coverage), pass 1 = (oct normal xy, snow capacity, coverage).
function bakeMaterial(atlas, B) {
  const aCardN = attribute('aCardN', 'vec3'), aVeg = attribute('aVeg', 'vec4'), aR = attribute('aR', 'vec3'), aU = attribute('aU', 'vec3'), aD = attribute('aD', 'vec3'), aCell = attribute('aCell', 'vec2');
  const nG = attribute('normal', 'vec3');
  const vN = varying(normalize(vec3(nG.x, nG.y.div(B.uAspect), nG.z).add(vec3(0, 1e-6, 0))), 'vgN');
  const vUp = varying(normalize(vec3(aCardN.x, aCardN.y.div(B.uAspect), aCardN.z).add(vec3(0, 1e-6, 0))).y, 'vgUp');
  // the card faces this view if its (normalised-space) normal points towards the view direction
  const vFront = varying(select(dot(vec3(aCardN.x, aCardN.y.mul(0.5), aCardN.z), aD).greaterThanEqual(0), one, -1), 'vgFront');
  const p = vec3(positionGeometry.x, positionGeometry.y.mul(2).sub(1), positionGeometry.z); // unit tree -> normalised space
  const z = dot(p, aD).negate().div(B.uRf).mul(0.95); // -0.95..0.95, nearer = smaller; written as 0.5 +- z/2 (both clip conventions)
  const zc = Fn((builder) => (builder.renderer.reversedDepthBuffer ? float(0.5).sub(z.mul(0.5)) : z.mul(0.5).add(0.5)), 'float')();
  const vUv = uv(), t = texture(atlas.map, vUv), A4 = texture(atlas.aux, vUv), mip = vegMip(vUv.mul(AT));
  let col = t.rgb.mul(mix(vec3(1), B.uNeedleTint, step(0.5, aVeg.y)));
  const leaf = leafMask(B, A4, mip); // WS-42: same mip boost as the near shader (WS-76: same leaf rules)
  col = mix(col, B.uLeafCol.mul(leafShade(B, A4)), leaf).mul(attribute('color', 'vec3')); // the reference colour: the impostor tints it per tree
  // same coverage rule as the near model at the same screen size (its A2C + mip boost), so the hand-over matches
  const alpha = clamp(max(t.a.mul(mip.mul(0.34).add(1)), leaf), 0, 1);
  const up = vUp.mul(vFront);
  const cap = select(A4.r.greaterThan(0.004), vegCap(A4.r, up, aVeg.x), 0);
  // snow share at the current load, exactly as the near shader computes it at this screen size; a share averages
  // correctly through the MSAA resolve and the mips (a threshold does not: averaged thresholds lose the snow)
  const sw = mip.mul(0.05).add(0.05).mul(B.uSnowHq.x);
  const snow0 = select(cap.greaterThan(0.004).and(B.uLoad.greaterThan(0.001)), smoothstep(one.sub(cap).sub(sw), one.sub(cap).add(sw), B.uLoad), 0); // WS-35: same load-0 guard
  const snowF = max(snow0, B.uSnowHq.y.mul(A4.r).mul(smoothstep(0.15, 0.85, up)).mul(min(B.uLoad.mul(4), 1))); // WS-34 hq: same frost dusting
  const n = normalize(vN), nn = select(aVeg.y.lessThan(0.5).and(vFront.lessThan(0)), n.negate(), n);
  // premultiplied by alpha: alpha-to-coverage also WRITES alpha to the covered samples, so the resolve gives coverage x
  // alpha (~alpha^2); premultiplying keeps every ratio (colour, normal, snow) exact and opacity = sqrt(a)
  const out = select(B.uPass.lessThan(0.5), vec4(col.mul(alpha), alpha), vec4(octEncY(nn).mul(0.5).add(0.5).mul(alpha), snowF.mul(alpha), alpha));
  const m = new THREE.NodeMaterial();
  m.side = THREE.DoubleSide; m.alphaToCoverage = true; m.fog = false; m.lights = false;
  m.vertexNode = vec4(aCell.add(vec2(dot(p, aR), dot(p, aU)).div(B.uRf).mul(B.uHalf)).sub(B.uTile.xy).mul(B.uTile.zw), zc, 1); // VIS-239: uTile = the cell drawn (trees.js bake)
  m.fragmentNode = Fn(() => { alpha.lessThan(0.02).discard(); return out; })();
  return m;
}

// VIS-267: the forest's mean from the cells' own 1 x 1 mips (createTreeKit bakeMean). px = cols x rows texels, tile k =
// r * cols + c, rgba8 as the pictures store them (pass 0: sRGB colour + linear coverage; pass 1 linear). Goes on with the
// mip chain the full picture would have had from its one-texel-per-cell level down to 1 x 1, as three's WebGPU mip pass
// samples (bilinear at each new texel's centre, clamped, decoded and stored again in 8 bits per level). Returns the
// 1 x 1 mip's 4 bytes as stored; meanRead's maths stay on the GPU (its own sRGB decode: the exact formula read 59.6 where
// the GPU wrote 59). An exact half step is stored DOWN, as the GPU stores it (measured 2026-10-25, Aurora: coverages 59
// and 16 average 37.5 -> 37, 28 + 7 -> 17; rounding it up read the cover one step high, the albedo one or two low).
const s2l = (c) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const l2s = (c) => (c <= 0.0031308 ? c * 12.92 : 1.055 * c ** (1 / 2.4) - 0.055);
const b8 = (v) => Math.max(0, Math.round(clamp01(v) * 255 - 1e-6));
export function cellsMean(px, cols, rows, pass) {
  const srgb = pass === 0, dec = (b, ch) => (srgb && ch < 3 ? s2l(b / 255) : b / 255), enc = (v, ch) => (srgb && ch < 3 ? b8(l2s(clamp01(v))) : b8(v));
  let w = cols, h = rows, L = new Float64Array(w * h * 4);
  for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) for (let ch = 0; ch < 4; ch++) L[((rows - 1 - r) * w + c) * 4 + ch] = dec(px[(r * cols + c) * 4 + ch], ch); // row 0 = clip y +1 (bakeInto)
  const tap = (s, n) => { const i = Math.floor(s); return [Math.min(n - 1, Math.max(0, i)), Math.min(n - 1, Math.max(0, i + 1)), s - i]; };
  while (w > 1 || h > 1) {
    const w2 = Math.max(1, w >> 1), h2 = Math.max(1, h >> 1), L2 = new Float64Array(w2 * h2 * 4);
    for (let j = 0; j < h2; j++) {
      const [y0, y1, fy] = tap(((j + 0.5) * h) / h2 - 0.5, h);
      for (let i = 0; i < w2; i++) {
        const [x0, x1, fx] = tap(((i + 0.5) * w) / w2 - 0.5, w), at = (x, y, ch) => L[(y * w + x) * 4 + ch];
        for (let ch = 0; ch < 4; ch++) L2[(j * w2 + i) * 4 + ch] = dec(enc(lerp(lerp(at(x0, y0, ch), at(x1, y0, ch), fx), lerp(at(x0, y1, ch), at(x1, y1, ch), fx), fy), ch), ch);
      }
    }
    L = L2; w = w2; h = h2;
  }
  return [0, 1, 2, 3].map((ch) => enc(L[ch], ch));
}

// ------------------------------------------------------------------ 4. kit: models + materials + impostor bake
/**
 * createTreeKit({ renderer, direction: 'cards'|'stylised', season, impostor: { frames, px }, variants: 2 })
 * -> { V, variants: [{ species, geo, H, R, aspect, rf }], hiMat, impMat, U, rebake(), setDirection(), dispose() }
 * WS-75: the renderer is a WebGPURenderer after `await renderer.init()`. hiMat / impMat also draw their own sun shadow
 * (castShadowPositionNode + maskShadowNode): there are no separate depth materials any more (hiDepth / impDepth null).
 */
export function createTreeKit(o) {
  const renderer = o.renderer;
  const tAt = performance.now();
  const atlas = buildAtlas(o.atlasData || prepareAtlasSync(o.hq));
  o.atlasData = null; delete atlas.data; // VIS-267 round 2: the pixels are the two textures' own (atlasOut below)
  const timing = { atlas: Math.round(performance.now() - tAt) };
  const U = {
    // WS-75 perf: the camera changes every frame and is the same for every tree mesh: render group = one write a
    // render (an object-group uniform is written again for each impostor cell and near-tree mesh)
    uCam: uniform(new THREE.Vector3()).setGroup(renderGroup), uLod: uniform(new THREE.Vector4(120, 25, 1, 0)),
    uDensity: uniform(1.01), uFocus: uniform(new THREE.Vector4(0, 0, 0, 0)),
    uSnow: uniform(new THREE.Vector4(0.3, 0, 0, 0)), uSnowCol: uniform(new THREE.Color('#eef2f7')),
    uSeason: uniform(new THREE.Vector4(0, 0, 0, 0)), uLeafCol: uniform(new THREE.Color('#5f8a34')), uNeedleTint: uniform(new THREE.Vector3(1, 1, 1)),
    uImp: uniform(new THREE.Vector4(8, 0, 0.9, 0.9)), uImpRf: uniformArray(new Array(8).fill(1), 'float').setGroup(renderGroup), /* WS-75 perf: written once a render, not once per impostor cell */ uImpAtlas: uniform(new THREE.Vector4(1, 1, 1024, 0)), uImpDbg: uniform(new THREE.Vector4(0, 0, 0, 0)),
    uHaze: uniform(new THREE.Vector4(80, 1100, 0.3, 0)).setGroup(renderGroup), uThin: uniform(new THREE.Vector4(0.25, 450, 1400, 0)), uA2C: uniform(1), uImpSun: uniform(0.55),
    uSnowHq: uniform(new THREE.Vector4(1, 0, 0, 0)), // WS-34 hq (neutral = today's look)
    uFogCol: uniform(new THREE.Color(0xffffff)).setGroup(renderGroup), uFogOn: uniform(0).setGroup(renderGroup), // WS-75: the scene fog's colour for the haze (set by the layer each frame). VIS-267: the haze trio is shared (renderGroup): the far landscape reads it, and its own group is not uploaded on the rescue set
    uA2CStep: uniform(0.25), // WS-75: the alpha-to-coverage dither (one coverage step of 4x MSAA; 0 = off)
    // WS-76 birch leaves: (cut, shade, palette size, variants per species) + the season's palette (hq.birch.summer / autumn)
    uLeafHq: uniform(new THREE.Vector4(0, 0, 0, o.variants ?? 2)), uLeafPal: uniformArray(Array.from({ length: 8 }, () => new THREE.Vector4(1, 1, 1, 1)), 'vec4'),
    uSw: uniform(new THREE.Vector3(1, 10, 1 / 0.3)).setGroup(renderGroup), // lazy/kittrees.js's tree swap (on, the clock in s, 1 / swap s), made here for the same reason as uHand
    uHand: uniform(new THREE.Vector4(1, 0, 1, 0)).setGroup(renderGroup), // VIS-267: lazy/kittrees.js's hand-over band (its numbers there), made here with the scene so its place among the shared uniforms never depends on when the kit arrived
    uHeroFade: uniform(new THREE.Vector4(0, 0, 0, 0)), // VIS-176: (kit trees' share 0..1, species 0 / 1 / 2 has a kit tree): lazy/kittrees.js take-over cross-fade
  };
  const HQ = o.hq || {}; // WS-34: vegetation.trees.hq (preview-gated quality options; absent = unchanged)
  if (HQ.snow) U.uSnowHq.value.set(HQ.snow.soft ?? 1, HQ.snow.dust ?? 0, 0, 0);
  const LV = HQ.birch?.fans === 'fine' && HQ.birch.leaves; // WS-76: the atlas draws the leaf clusters only with the fine fans
  if (LV) U.uLeafHq.value.set(LV.cut ?? 0.25, LV.shade ?? 1, 0, o.variants ?? 2);
  // the season's uniforms; WS-76: a palette for this season (hq.birch[season name]) gives each birch its own leaf colour,
  // and the reference colour (bake, canopy tone) is the palette's mean
  const seasonU = () => {
    const s = kit.season, P = Array.isArray(HQ.birch?.[s.name]) ? HQ.birch[s.name].slice(0, 8) : [];
    U.uSeason.value.x = s.foliage; U.uNeedleTint.value.set(...s.needle);
    if (!P.length) U.uLeafCol.value.set(s.leaf);
    else {
      const c = new THREE.Color(), m = [0, 0, 0];
      P.forEach((h, i) => { c.set(h); U.uLeafPal.array[i].set(c.r, c.g, c.b, 1); m[0] += c.r / P.length; m[1] += c.g / P.length; m[2] += c.b / P.length; });
      U.uLeafCol.value.setRGB(...m);
    }
    U.uLeafHq.value.z = P.length;
  };
  // the impostor atlases (render targets, made by the first bake): texture nodes made now, pointed at them after each bake
  const texSlot = () => { const nodes = []; const f = (uvq) => { const t = texture(PLACEHOLDER, uvq); nodes.push(t); return t; }; f.set = (tx) => nodes.forEach((t) => { t.value = tx; }); return f; };
  const kit = { U, atlas, variants: [], direction: null, bakedLoad: o.snowLoad ?? seasonParams(o.season || 'winter').snowLoad, season: seasonParams(o.season || 'winter'), tex: { a: texSlot(), n: texSlot() } };
  const nVar = o.variants ?? 2;
  const frames = o.impostor?.frames ?? 8, px = o.impostor?.px ?? 128;
  U.uImp.value.x = frames; U.uImp.value.y = px;

  kit.hiMat = hiMaterial(U, atlas, { a2c: o.a2c !== false });
  kit.hiDepth = null;
  kit.impMat = impostorMaterial(U, kit.tex);
  kit.impDepth = null;
  kit.setA2C = (on) => { kit.hiMat.alphaToCoverage = !!on; kit.hiMat.needsUpdate = true; kit.impMat.alphaToCoverage = !!on; kit.impMat.needsUpdate = true; U.uA2C.value = on ? 1 : 0; };
  kit.setA2C(o.a2c !== false);
  kit.colorFor = (si) => SPECIES_COL[SPECIES[si]];

  function build(direction) {
    kit.variants.forEach((v) => v.geo.dispose());
    const make = DIRECTIONS[direction].make;
    kit.variants = [];
    SPECIES.forEach((sp, si) => {
      for (let k = 0; k < nVar; k++) {
        const m = make[si](11 + k * 37 + si * 101, direction === 'cards' ? HQ : {}, false, k); // WS-34: hq crown detail (realistic direction only); k: the variant (hq.birch.forms)
        // bounding radius in normalised space (crown radius 1, half height 1, centred)
        const p = m.geo.attributes.position; let rn = 0;
        for (let i = 0; i < p.count; i++) rn = Math.max(rn, Math.hypot(p.getX(i), p.getY(i) * 2 - 1, p.getZ(i)));
        kit.variants.push({ species: si, name: `${sp}-${k}`, geo: m.geo, H: m.H, R: m.R, aspect: m.H / m.R, rf: rn * 1.03 });
      }
    });
    kit.variants.forEach((v, i) => { U.uImpRf.array[i] = v.rf; });
    kit.direction = direction;
  }

  // --- impostor bake (WS-18 r2: instanced views, 4x MSAA + alpha-to-coverage, premultiplied, one resolve per pass)
  // WS-72 (begin): rtA / rtN are the RESULTS only (colour + mips, no depth, no MSAA). Each pass renders into an MSAA
  // scratch target, its resolved level 0 is copied into the result (mips rebuilt there) and the scratch is freed at
  // once: a target keeps its multisampled colour + depth buffers for as long as it lives (~430 MB on desktop for 64 MB
  // of atlases). A re-bake (season, snow load) allocates it again.
  let rtA = null, rtN = null, bakeSet = null;
  const SAMPLES = o.bakeSamples ?? 4;
  const bakeU = { uTile: uniform(new THREE.Vector4(0, 0, 1, 1)), uPass: uniform(0), uAspect: uniform(6), uRf: uniform(1), uHalf: uniform(new THREE.Vector2(0.1, 0.1)), uLoad: uniform(0),
    uSeason: uniform(new THREE.Vector4()), uLeafCol: uniform(new THREE.Color()), uNeedleTint: uniform(new THREE.Vector3(1, 1, 1)), uSnowHq: uniform(new THREE.Vector4(1, 0, 0, 0)), uLeafHq: U.uLeafHq };
  // VIS-239 tiles: each variant's cell is drawn into an MSAA scratch of one cell (uTile maps the atlas's clip space onto
  // it) and copied into place, so the 4x MSAA colour + depth is 1024 x 1024 (~32 MB) for a moment, not the whole atlas
  // (~200 MB)
  const layout = () => {
    const V = kit.variants.length, cols = Math.min(3, V), rows = Math.ceil(V / cols), cell = frames * px;
    return { cols, rows, cell, W: cols * cell, Hh: rows * cell };
  };
  const pictureOpt = { generateMipmaps: true, minFilter: THREE.LinearMipmapLinearFilter, magFilter: THREE.LinearFilter, depthBuffer: false, samples: 0 };
  const picture = (w, h, srgb) => { const t = new THREE.RenderTarget(w, h, { ...pictureOpt, ...(srgb ? { colorSpace: THREE.SRGBColorSpace } : {}) }); renderer.initRenderTarget(t); return t; }; // copyTextureToTexture needs the storage
  // VIS-267 (season switch): what a bake draws with besides the trees themselves (the kit's direction): the season's
  // foliage, reference leaf colour and needle tint, the snow load, the hq snow softness. bakeIn() = the kit's own season
  // and load; bakeIn(season, load) = another season's (an idle bake ahead of a switch: kit.prebake), the leaf colour
  // worked out as seasonU does, without touching the uniforms the drawn trees read
  const leafOf = (s) => {
    const P = Array.isArray(HQ.birch?.[s.name]) ? HQ.birch[s.name].slice(0, 8) : [];
    if (!P.length) return new THREE.Color(s.leaf);
    const c = new THREE.Color(), m = [0, 0, 0];
    P.forEach((h) => { c.set(h); m[0] += c.r / P.length; m[1] += c.g / P.length; m[2] += c.b / P.length; });
    return new THREE.Color().setRGB(...m);
  };
  const bakeIn = (s = null, load = kit.bakedLoad) => {
    const S = s || kit.season;
    return { foliage: S.foliage, leaf: s ? leafOf(s) : U.uLeafCol.value.clone(), needle: [...S.needle], load, snowHq: U.uSnowHq.value.clone() };
  };
  const keyOf = (I) => [kit.direction, frames, px, I.foliage, I.leaf.r, I.leaf.g, I.leaf.b, ...I.needle, I.load, I.snowHq.x, I.snowHq.y, U.uLeafHq.value.x, U.uLeafHq.value.y].join('|');
  // the bake's uniforms from its inputs (bakeIn), and its scene (kept between bakes: re-bakes on season and
  // snow changes reuse its compiled materials; its geometries share the variants' vertex buffer, so they are freed only
  // when the variants are rebuilt)
  function bakeSetup(L, I = bakeIn()) {
    const { cols, cell, W, Hh } = L, fpx = px;
    bakeU.uSeason.value.set(I.foliage, 0, 0, 0); bakeU.uLeafCol.value.copy(I.leaf); bakeU.uNeedleTint.value.set(...I.needle); // WS-76: the season's reference leaf colour
    bakeU.uLoad.value = I.load; bakeU.uSnowHq.value.copy(I.snowHq); // WS-34 hq: the impostors bake the same snow softness + dusting
    const bkey = `${kit.direction}|${W}x${Hh}`;
    if (bakeSet?.key === bkey) return;
    bakeSet?.mats.forEach((m) => m.dispose()); bakeSet?.geos.forEach((g) => g.dispose());
    bakeSet = { key: bkey, scene: new THREE.Scene(), mats: [], geos: [] };
    const { scene, mats, geos } = bakeSet;
    kit.variants.forEach((v, vi) => {
      const g = new THREE.InstancedBufferGeometry();
      for (const k of ['position', 'normal', 'uv', 'color', 'aCardN', 'aVeg']) g.setAttribute(k, v.geo.attributes[k]);
      g.setIndex(v.geo.index);
      const n = frames * frames, R = new Float32Array(n * 3), Up = new Float32Array(n * 3), D = new Float32Array(n * 3), Cl = new Float32Array(n * 2);
      const ox = (vi % cols) * cell, oy = Math.floor(vi / cols) * cell;
      for (let j = 0; j < frames; j++) for (let i = 0; i < frames; i++) {
        const k = j * frames + i;
        const u = new THREE.Vector2(i / (frames - 1) * 2 - 1, j / (frames - 1) * 2 - 1);
        const t = new THREE.Vector2((u.x + u.y) * 0.5, (u.x - u.y) * 0.5);
        const Dv = new V3(t.x, Math.max(1 - Math.abs(t.x) - Math.abs(t.y), 0) + 1e-4, t.y).normalize();
        let r = new V3().crossVectors(new V3(0, 1, 0), Dv); r = r.length() > 1e-4 ? r.normalize() : new V3(1, 0, 0);
        const up = new V3().crossVectors(Dv, r);
        r.toArray(R, k * 3); up.toArray(Up, k * 3); Dv.toArray(D, k * 3);
        Cl[k * 2] = ((ox + (i + 0.5) * fpx) / W) * 2 - 1; Cl[k * 2 + 1] = ((oy + (j + 0.5) * fpx) / Hh) * 2 - 1;
      }
      g.setAttribute('aR', new THREE.InstancedBufferAttribute(R, 3)); g.setAttribute('aU', new THREE.InstancedBufferAttribute(Up, 3));
      g.setAttribute('aD', new THREE.InstancedBufferAttribute(D, 3)); g.setAttribute('aCell', new THREE.InstancedBufferAttribute(Cl, 2));
      g.instanceCount = n;
      // one material per variant (its aspect, radius); the season, load and pass are shared uniforms
      const B = { ...bakeU, uAspect: uniform(v.aspect), uRf: uniform(v.rf), uHalf: uniform(new THREE.Vector2(fpx / W, fpx / Hh)) };
      const m = bakeMaterial(atlas, B);
      const mesh = new THREE.Mesh(g, m); mesh.frustumCulled = false; scene.add(mesh);
      mats.push(m); geos.push(g);
    });
  }
  // one pass (0 = albedo, 1 = normal + snow) into dst: cell by cell through the MSAA scratch, freed at the end (WS-72).
  // VIS-267: with `each` (no dst) every cell is one cell-size picture with mips that each(picture, k) reads. VIS-267
  // lossless: that picture IS the scratch's resolve (level 0 resolved in place, its mips made by the same pass three's
  // copy made them with: the same bytes, no 4 MB copy beside it), and the MSAA colour + depth, cleared by every cell and
  // never read after its resolve, are not stored (a GPU with transient attachments keeps them in tile memory)
  const bakeCam = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1); // unused by the bake material (it writes clip space)
  function bakeInto(dst, pass, L, each = null) {
    const { rows, cols, cell, W, Hh } = L;
    const prev = { rt: renderer.getRenderTarget(), auto: renderer.autoClear, cc: renderer.getClearColor(new THREE.Color()), ca: renderer.getClearAlpha(), sh: renderer.shadowMap.autoUpdate };
    renderer.autoClear = true; renderer.shadowMap.autoUpdate = false; // an off-screen bake: the sun's shadow maps stay as they are (shadows.js)
    renderer.setClearColor(new THREE.Color(0, 0, 0), 0); // premultiplied: empty = 0
    const tiles = []; for (let r = 0; r < rows; r++) for (let c = 0; c < cols; c++) tiles.push([c * cell, r * cell]);
    const at = new THREE.Vector2();
    const rt = new THREE.RenderTarget(cell, cell, { ...(each ? pictureOpt : { generateMipmaps: false, minFilter: THREE.LinearFilter, magFilter: THREE.LinearFilter }), depthBuffer: true, samples: SAMPLES,
      colorSpace: dst ? dst.texture.colorSpace : pass === 0 ? THREE.SRGBColorSpace : THREE.NoColorSpace, storeMultisampledColorBuffer: false, storeMultisampledDepthBuffer: false }); // WS-72: scratch, same format as dst
    if (each) { renderer.initRenderTarget(rt); rt.texture.generateMipmaps = false; } // (its levels made; its mips after each resolve, below: three's WebGL2 path would make them before it)
    bakeU.uPass.value = pass;
    try {
      tiles.forEach(([ox, oy], k) => {
        bakeU.uTile.value.set(((ox + cell / 2) / W) * 2 - 1, ((oy + cell / 2) / Hh) * 2 - 1, W / cell, Hh / cell);
        renderer.setRenderTarget(rt); // (each draws elsewhere)
        renderer.render(bakeSet.scene, bakeCam); // clears, draws, resolves the MSAA buffer
        if (each) { renderer.backend.generateMipmaps?.(rt.texture); each(rt, k); return; } // (the mips as three's copy made them: the same pass)
        dst.texture.generateMipmaps = k === tiles.length - 1; // dst's mips once, after the last cell
        renderer.copyTextureToTexture(rt.texture, dst.texture, null, at.set(ox, Hh - oy - cell)); // WS-72: level 0 copied exactly (row 0 = clip y +1)
      });
      if (dst) dst.texture.generateMipmaps = true;
    } finally {
      rt.dispose(); // WS-72: frees the MSAA colour + depth buffers (each: and the picture; its draws and reads are submitted)
      bakeU.uTile.value.set(0, 0, 1, 1);
      renderer.setRenderTarget(prev.rt); renderer.autoClear = prev.auto; renderer.setClearColor(prev.cc, prev.ca); renderer.shadowMap.autoUpdate = prev.sh;
    }
  }
  // the full bake (today's trees are drawn): both atlases kept as the impostors' pictures
  let rtFreed = false; // VIS-267: the pictures' storage freed (kit.retire), the targets kept: their texture nodes still name them
  function bake() {
    const L = layout(), { cols, rows, W, Hh } = L;
    if (!rtA || rtA.width !== W || rtA.height !== Hh) { rtA?.dispose(); rtN?.dispose(); rtA = picture(W, Hh, true); rtN = picture(W, Hh, false); }
    else if (rtFreed) { renderer.initRenderTarget(rtA); renderer.initRenderTarget(rtN); }
    rtFreed = false;
    U.uImpAtlas.value.set(cols, rows, W, Hh);
    const I = bakeIn(), key = keyOf(I);
    bakeSetup(L, I);
    bakeInto(rtA, 0, L); bakeInto(rtN, 1, L);
    kit.tex.a.set(rtA.texture); kit.tex.n.set(rtN.texture);
    kit.bakeInfo = { W, H: Hh, frames, px, samples: SAMPLES, bytes: W * Hh * 4 * 2 * 4 / 3 };
    const seq = (kit.bakeSeq = (kit.bakeSeq || 0) + 1);
    Promise.all([meanRead(rtA.texture, 0), meanRead(rtN.texture, 1)]).then(([pA, pN]) => { const m = meanOf(pA, pN); meanCache.set(key, m); if (seq !== kit.bakeSeq) return; kit.mean = m; kit.onBake?.(); }).catch((e) => console.warn('[trees] forest colour not read', e));
  }
  // VIS-239 mean only: today's trees are not drawn (a tree kit draws instead: o.defer), only the forest's mean colour is
  // needed (the landscape's canopy tone, kit.mean). (A bake at a quarter of the size read the snow share 0.28 for 0.40;
  // an offline value cannot replace it: these are the procedural trees of the resort's own settings, season and snow
  // load, not the kit pack's.) VIS-267 step 2 (phone start memory): the same trees, cells, passes and maths as the full
  // bake, but no full-size picture: each cell is copied into ONE cell-size picture (its mips rebuilt), that picture's
  // 1 x 1 mip is drawn into pixel k of a cells x 1 target as stored (cellRead), the CPU finishes the mip chain over the
  // cells (cellsMean) and meanRead does its maths on that 1 x 1 mip, uploaded (mipTex). Exact, not an estimate: the
  // cells tile the full picture at power-of-two offsets (cell = frames x px = 1024), so the full picture's mip at one
  // texel per cell holds these very cell means, 8-bit sRGB per level as stored (browser: the same bytes); from there to
  // 1 x 1 the GPU's chain is copied as it samples (bilinear at the texel centre, NPOT included: 3 x 2 -> 1 x 1 reads the
  // middle column only, today's canopy tone; Aurora winter, summer, autumn: kit.mean to the bit). Peak: the 4x
  // MSAA scratch (colour + depth 32 MB) + the cell picture with mips it resolves into (5.3 MB) ~ 37 MB instead of the
  // ~68 MB of a 3072 x 2048 picture + scratch. Both passes one after another, the GPU done with one before the next
  // (gpuIdle). Bakes run one after another; a newer request (season, snow load, the full bake) makes an older one stop
  // at its next step. While the kit draws, the procedural trees' parts are made for the bake and freed after the last
  // one queued (release). The cell picture goes as soon as its cells are drawn (the GPU has the work: a readback waits
  // behind a season's uploads, and the picture lived ~1 s for nothing).
  // VIS-267 (season switch): each result is kept by its inputs (keyOf), so a switch to a season baked before, or baked
  // ahead while the map idled (kit.prebake: main.js after Ready), takes its colour at once and bakes nothing; a bake a
  // switch still needs waits for the season's kit pictures to be on the GPU (o.busy: lazy/kittrees.js) and for the GPU,
  // so its 41 MB never meet the far upload. Same bytes as a bake now: a bake is a function of these inputs alone.
  // Not in the package: the colour is the resort's own procedural trees at its season and snow load.
  let meanJob = Promise.resolve(), meanWaiting = 0;
  const meanCache = new Map();
  const gpuIdle = () => gpuDone(renderer); // WebGPU: the freed pictures are gone before the next is made
  const queueMean = (fn) => {
    meanWaiting++;
    meanJob = meanJob.then(fn).catch((e) => console.warn('[trees] forest colour not read', e)).finally(() => { if (--meanWaiting === 0) release(); });
    return meanJob;
  };
  // the two passes of one mean bake with inputs I; null when stop() turned true first (a newer request) or the device is lost.
  // VIS-267: each pass's programs side by side, off the main thread, one cell dry before its first draw (dryDraw), per
  // bake scene (release frees its materials: a bake after Ready, main.js seasonWarm's prebake, builds them again) and
  // per pass (another target format: pipelines of its own); a link while drawing took 16-27 ms on the phone set
  async function meanPasses(I, stop) {
    const px8 = [];
    let ms = 0;
    await atlasBack(); // VIS-267 round 2: drawn again (worker) if it went after the last bake (atlasOut)
    for (const pass of [0, 1]) {
      // VIS-267: after Ready a pass (its 4x MSAA scratch, ~41 MB for a moment) waits its turn in the lane (lane.js): never
      // on top of a season's kit pictures or a light-casting redraw. (Taken here, after bakeMean's wait for the kit's
      // pictures: the kit's load waits for the lane, so a bake holding it while it waits for the kit would wait for ever.)
      const end = await lane.turn('forest colour');
      try {
        if (stop() || deviceLost(renderer)) return null; // VIS-267: no tile draws or copies on a lost device
        parts(); // VIS-267: the variants again if the last mean bake freed them
        const t0 = performance.now(), L = layout(), n = L.cols * L.rows;
        bakeSetup(L, I); // (again for the second pass: a full bake in between may have set the uniforms)
        const dried = (bakeSet.dried ??= new Set()); // (above)
        if (!dried.has(pass)) {
          dried.add(pass);
          await dryDraw(renderer, () => bakeInto(null, pass, { ...L, rows: 1, cols: 1 }, (pic) => cellRead(pic, pass, 0, 1)));
          if (stop() || deviceLost(renderer)) return null;
        }
        bakeInto(null, pass, L, (pic, k) => cellRead(pic, pass, k, n)); // (the cell picture goes with the scratch: the GPU finishes its draws and reads first)
        ms += performance.now() - t0;
        px8[pass] = await meanRead(mipTex(pass, cellsMean(await cellsRead(pass, n), L.cols, L.rows, pass)), pass, 4);
        await gpuIdle();
      } finally { end(); } // (the lane goes once the GPU has let go of the pass's pictures, or the pass stopped or failed)
    }
    timing.meanBake = Math.round(ms);
    return meanOf(px8[0], px8[1]);
  }
  const fromCache = () => { const m = meanCache.get(keyOf(bakeIn())); if (!m) return false; kit.mean = m; kit.onBake?.(); return true; };
  function bakeMean() {
    const seq = (kit.bakeSeq = (kit.bakeSeq || 0) + 1), live = () => seq === kit.bakeSeq;
    if (fromCache()) return;
    queueMean(async () => {
      if (!live() || fromCache()) return;
      await o.busy?.(); // a season's kit pictures first, then the GPU done with them
      if (!live() || fromCache()) return;
      await gpuIdle();
      const I = bakeIn(), key = keyOf(I), m = await meanPasses(I, () => !live());
      if (!m) return;
      meanCache.set(key, m);
      if (live()) { kit.mean = m; kit.onBake?.(); }
    });
  }
  /** VIS-267: the forest's mean for another season (its params) at a snow load, baked now for a switch later; a request
   *  of the kit's own (a switch) stops it at its next step. -> Promise<true when that colour is kept> */
  kit.prebake = (s, load) => {
    const S = typeof s === 'string' ? seasonParams(s) : s, I = bakeIn(S, load), key = keyOf(I), seq = kit.bakeSeq;
    if (meanCache.has(key)) return Promise.resolve(true);
    return queueMean(async () => {
      if (meanCache.has(key) || seq !== kit.bakeSeq) return;
      await gpuIdle();
      const m = await meanPasses(I, () => seq !== kit.bakeSeq);
      if (m) meanCache.set(key, m);
    }).then(() => meanCache.has(key));
  };
  kit.meanCached = (s, load) => meanCache.has(keyOf(bakeIn(typeof s === 'string' ? seasonParams(s) : s, load)));
  // VIS-267: every read of a picture's 1 x 1 mip is one text with a mode uniform, so an iPhone's first visit compiles one
  // program (a texture in a new program costs it about 290 ms). Slot 0 / 1: a cell's mip as stored (colour: sRGB-encoded,
  // so the 8 bits read back are the picture's own); 2 / 3: the mean's maths on the full pictures; 4 / 5: the same on the
  // mip uploaded. Each slot keeps its material, texture node (built on a real picture) and cells x 1 rgba8 target, in a
  // node build of its own (three shares one build, textures included, between materials that hash alike; and a node
  // pointed at another picture of the same version and sampler is not bound again: the snow share read the albedo).
  const readers = [];
  function readMip(slot, tex, k = 0, n = 1) {
    let q = readers[slot];
    if (!q) {
      const x = texture(tex, vec2(0.5), float(30)), uMode = uniform(slot > 3 ? slot - 2 : slot), uK = uniform(0), uN = uniform(1), a = max(x.a, 1e-4);
      const m = new THREE.NodeMaterial(); m.depthTest = false; m.depthWrite = false; m.fog = false; m.lights = false;
      m.customProgramCacheKey = () => `mip-read-${slot}`;
      m.vertexNode = vec4(positionGeometry.x.add(1).add(uK.mul(2)).div(uN).sub(1), positionGeometry.y, 0, 1); // the quad on pixel k of n
      m.fragmentNode = select(uMode.lessThan(0.5), vec4(sRGBTransferOETF(clamp(x.rgb, 0, 1)), x.a), select(uMode.lessThan(1.5), x, select(uMode.lessThan(2.5), vec4(sqrt(clamp(x.rgb.div(a), 0, 1)), x.a), vec4(clamp(x.b.div(a), 0, 1), x.a, 0, 1))));
      const s = new THREE.Scene(), mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), m); mesh.frustumCulled = false; s.add(mesh);
      q = readers[slot] = { s, x, uK, uN, rt: new THREE.RenderTarget(n, 1, { depthBuffer: false }), cam: new THREE.OrthographicCamera() };
    }
    if (q.rt.width !== n) q.rt.setSize(n, 1);
    q.x.value = tex; q.uK.value = k; q.uN.value = n;
    renderer.setRenderTarget(q.rt); renderer.render(q.s, q.cam);
    return q.rt;
  }
  // a cell picture's mip into pixel k, read once per pass
  function cellRead(pic, pass, k, n) {
    const auto = renderer.autoClear; renderer.autoClear = false; // the other cells' pixels stay
    readMip(pass, pic.texture, k, n);
    renderer.autoClear = auto;
  }
  // the 1 x 1 mip cellsMean gives, as a 1 x 1 picture of the pass's format (pass 0 sRGB: the GPU decodes it as before)
  const mipT = [null, null];
  function mipTex(pass, px) {
    let t = mipT[pass];
    if (!t) { t = mipT[pass] = new THREE.DataTexture(new Uint8Array(4), 1, 1); t.colorSpace = pass === 0 ? THREE.SRGBColorSpace : THREE.NoColorSpace; t.minFilter = t.magFilter = THREE.LinearFilter; }
    t.image.data.set(px); t.needsUpdate = true;
    return t;
  }
  async function cellsRead(pass, n) {
    if (deviceLost(renderer)) throw new Error('graphics device lost'); // VIS-267: the bake's caller keeps the colour it has
    return renderer.readRenderTargetPixelsAsync(readers[pass].rt, 0, 0, n, 1);
  }
  // VIS-267: what a bake draws, made when it is needed (variants: ~14k vertices, 1 MB, ~15-45 ms here; the atlas
  // DataTextures keep their pixels, so a freed one uploads again on its next draw) and freed after a mean bake while the
  // kit draws (!kit.live): its 6.7 MB atlas, the variants' 1 MB vertex buffers and the bake's materials. The full bake
  // (kit.ensure) keeps them all until the kit takes over again (kit.retire).
  function parts() {
    if (!atlasIn()) atlasSet(again.now()); // (kit.ensure: today's trees are wanted now; a mean bake drew it again first, atlasBack)
    if (kit.variants.length) return;
    const t0 = performance.now(); build(kit.direction); timing.build = Math.round(performance.now() - t0);
  }
  function release() {
    if (kit.live) return;
    bakeSet?.mats.forEach((m) => m.dispose()); bakeSet?.geos.forEach((g) => g.dispose()); bakeSet = null;
    kit.variants.forEach((v) => v.geo.dispose()); kit.variants = [];
    atlas.map.dispose(); atlas.aux.dispose();
    atlasOut();
  }
  // VIS-267 round 2 (phone memory): the atlas's pixels too (~5 MB on the CPU; its GPU copy goes above), 5 s after the
  // last mean bake while the kit draws (a run of bakes ahead keeps them), and drawn again before a bake or today's trees
  // need them: by a bake in the start's worker (atlasBack), by kit.ensure at once (parts). The same bytes: trees-atlas.js
  // draws on a CPU canvas (willReadFrequently) with no randomness, in the worker or here. (o.atlasAgain: a test's own)
  const again = o.atlasAgain || { now: () => prepareAtlasSync(o.hq), later: () => prepareAtlas(o.hq) };
  let atlasJob = null, atlasTimer = 0;
  const atlasIn = () => !!atlas.map.image?.data;
  const atlasSet = (d) => { for (const [t, px, n] of [[atlas.map, d.map, d.size], [atlas.aux, d.aux, d.auxSize]]) { t.image = { data: px, width: n, height: n }; t.needsUpdate = true; } };
  function atlasOut() {
    if (!o.release) return; // VIS-267: a visitor's page only (main.js RELEASE_CPU): ?edit and the editor's frame keep the pixels
    clearTimeout(atlasTimer);
    atlasTimer = setTimeout(() => {
      if (kit.live || meanWaiting || kit.variants.length || !atlasIn()) return; // (a bake or today's trees since)
      for (const t of [atlas.map, atlas.aux]) t.image = { data: null, width: t.image.width, height: t.image.height };
    }, 5000);
    atlasTimer?.unref?.(); // (node: a test's process does not wait for it)
  }
  const atlasBack = () => (atlasIn() ? Promise.resolve() : (atlasJob ||= Promise.resolve(again.later()).then((d) => { if (!atlasIn()) atlasSet(d); }).finally(() => { atlasJob = null; })));
  kit.atlasHeld = () => atlasIn(); // (QA, tests: the pixels are here)

  // r2: the forest's mean look as one colour (the smallest mip of the atlases): albedo, snow share at the baked load.
  // WS-75: read back asynchronously (WebGPU); the canopy tone follows a moment after the bake. VIS-267 W3: one read per
  // picture (pass 0: the albedo picture -> albedo, cover; pass 1: the normal picture -> snow share), each with its own
  // 1 x 1 target: the old 2 x 1 read with both pictures in one draw, the same maths per pixel. (slot: readMip's, per kind of picture)
  async function meanRead(tex, pass, slot = 2) {
    if (deviceLost(renderer)) throw new Error('graphics device lost'); // VIS-267: the bake's caller keeps the colour it has
    const prevRT = renderer.getRenderTarget();
    const sh = renderer.shadowMap.autoUpdate; renderer.shadowMap.autoUpdate = false;
    const rt = readMip(slot + pass, tex); renderer.setRenderTarget(prevRT); renderer.shadowMap.autoUpdate = sh;
    return renderer.readRenderTargetPixelsAsync(rt, 0, 0, 1, 1);
  }
  /** The colour from the two reads: albedo + cover from the albedo picture's, the snow share from the normal picture's. */
  const meanOf = (pA, pN) => { const f = (v) => (v / 255) ** 2; return { albedo: new THREE.Color(f(pA[0]), f(pA[1]), f(pA[2])), snow: pN[0] / 255, cover: pA[3] / 255 }; };

  // VIS-239: kit.live = the impostor pictures are baked (today's trees can be drawn). o.defer (createTreeLayer: a tree
  // kit draws instead) leaves them out: no full bake (64 MB of pictures to keep and ~200 MB of 4x MSAA scratch for a
  // moment, seconds on a phone), only the mean-colour bake (bakeMean); kit.ensure() bakes them the first time they are needed
  kit.live = !o.defer;
  const rebake = () => (kit.live ? bake() : bakeMean());
  kit.ensure = () => {
    if (kit.live) return false;
    kit.live = true;
    parts(); // VIS-267: (a mean bake may have freed them)
    const t0 = performance.now(); bake(); timing.bake = Math.round(performance.now() - t0); kit.lastBakeMs = timing.build + timing.bake;
    return true;
  };
  // VIS-267: the kit took over again (lazy/kittrees.js, after a season it had no pictures for): the impostor pictures' storage
  // (2 x 32 MB at 3072 x 2048) and the parts (atlas, variants, the bake's materials) go, as after a mean bake; the
  // targets stay (their texture nodes name them, nothing draws them: the layer hides its impostors) and kit.ensure()
  // bakes into them again if today's trees are needed once more. -> true when freed now
  kit.retire = () => {
    if (!kit.live) return false;
    kit.live = false;
    if (rtA) { rtA.dispose(); rtN.dispose(); rtFreed = true; }
    kit.bakeInfo = null;
    if (!meanWaiting) release();
    return true;
  };
  kit.setSeason = (s) => {
    kit.season = typeof s === 'string' ? seasonParams(s) : s;
    seasonU();
    if (kit.direction) rebake(); // (built once; VIS-267: the variants may be freed between mean bakes)
  };
  kit.setDirection = (d) => { if (!DIRECTIONS[d]) d = 'cards'; if (d === kit.direction) return false; const t0 = performance.now(); build(d); const t1 = performance.now(); rebake(); kit.lastBakeMs = performance.now() - t0; timing.build = Math.round(t1 - t0); timing[kit.live ? 'bake' : 'meanBake'] = Math.round(performance.now() - t1); return true; };
  kit.timing = timing;
  kit.setSnow = (load) => {
    U.uSnow.value.x = Math.min(0.92, Math.max(0, load));
    // the impostors store the snow share at one load: re-bake when the amount changes (0 = off, no bake needed)
    if (U.uSnow.value.x > 0.001 && Math.abs(U.uSnow.value.x - kit.bakedLoad) > 0.01) { kit.bakedLoad = U.uSnow.value.x; if (kit.direction) rebake(); }
  };
  kit.stats = () => kit.variants.map((v) => ({ name: v.name, tris: v.geo.index.count / 3, verts: v.geo.attributes.position.count }));
  kit.dispose = () => { kit.bakeSeq = (kit.bakeSeq || 0) + 1; /* a mean bake still running stops */ clearTimeout(atlasTimer); kit.variants.forEach((v) => v.geo.dispose()); rtA?.dispose(); rtN?.dispose(); readers.forEach((q) => q.rt.dispose()); mipT.forEach((t) => t?.dispose()); atlas.map.dispose(); atlas.aux.dispose(); };
  seasonU();
  kit.setDirection(o.direction || 'cards');
  kit.setSnow(o.snowLoad ?? kit.season.snowLoad);
  return kit;
}
export const TREE_DIRECTIONS = Object.fromEntries(Object.entries(DIRECTIONS).map(([k, v]) => [k, v.label]));
/** The 2 x 2 billboard quad of the impostors, position only (WS-75: the shaders build their own normal and uv). */
export function billboardQuad() { const q = new THREE.PlaneGeometry(2, 2); q.deleteAttribute('normal'); q.deleteAttribute('uv'); return q; }

// ------------------------------------------------------------------ 5. layer: instances, near set, cells
/**
 * createTreeLayer({ scene, renderer, pipeline, kit options, trees: { n, x, z, y (base), h, r, s (species), dom },
 *   cellOf(x, z) -> key, castShadowCell(key) -> bool, mobile, cfg })
 * Returns { update(camera), setDensity, setSnow, setSeason, setFocus, settings(), stats(), dispose }.
 * WS-32: the trees always draw the detailed model (cfg.model) at the full near distance, whatever the quality tier
 * (user, round 6: they must never drop to a simplified look on their own). There is no style setting; the tier only
 * decides alpha-to-coverage (needs MSAA) vs the dither, which looks the same at map scale.
 */
export function createTreeLayer(o) {
  const cfg = o.cfg || {}, T = o.trees, N = T.n;
  const imp = { frames: 8, px: 128, ...(cfg.impostor || {}) }; // VIS-233: phones bake the same pictures as desktop
  // WS-18 r3: snow on trees as levels (resort.json -> vegetation.snow = { default, levels: [{ id, label, load, grade }], ui });
  // a level may carry grade overrides (heavy = the brighter look of mid-winter snow-laden crowns). ui: false hides the
  // control and fixes the level (embeds, kiosks).
  const SL = cfg.snowLevels || {};
  const levels = SL.levels || SNOW_LEVELS;
  const nearestLevel = (v) => levels.reduce((b, l) => (Math.abs(l.load - v) < Math.abs(b.load - v) ? l : b), levels[0]);
  const lvl0 = levels.find((l) => l.id === SL.default) || nearestLevel(cfg.snow === false ? 0 : (cfg.snowLoad ?? 0.25));
  const st = {
    density: cfg.density ?? 0.25, season: cfg.season || 'winter',
    snowLevel: lvl0.id, snowAmt: lvl0.load,
  };
  // the viewer's amount and snow choice are remembered per browser; the config gives the defaults (WS-40: the amount
  // is a viewer setting again; WS-32 removed only the style, which stays ignored)
  const store = o.storeKey || null;
  try {
    const saved = store && JSON.parse(localStorage.getItem(store) || 'null');
    if (saved) {
      if (typeof saved.density === 'number') st.density = clamp01(saved.density); // WS-40; snow is the owner's (no guest control since 2026-10-28)
    }
  } catch { /* private mode */ }
  const save = () => {
    try { if (store && !api.noStore) localStorage.setItem(store, JSON.stringify({ density: st.density })); } catch { /* ignore */ }
    api.onSettings?.(st); // WS-49: the runtime follows the viewer's tree settings and remembers them under a preset (`store` off); noStore = not the viewer's own change
  };
  st.snow = st.snowAmt > 0.001;
  // VIS-79: { gradeK, grade } of the level (main.js withSnowGrade); null = the base grade as it is
  const levelGrade = () => { const L = levels.find((l) => l.id === st.snowLevel) || [...levels].reverse().find((l) => l.load <= st.snowAmt + 1e-3); return L?.gradeK || L?.grade ? { gradeK: L.gradeK || null, grade: L.grade || null } : null; };
  const a2c = () => (o.pipeline?.config?.samples ?? 4) > 0; // alpha-to-coverage needs MSAA (the Battery tier has none)
  const detailedModel = cfg.model || 'cards';
  const season = () => seasonParams(st.season, cfg.seasonParams);
  const tLayer = performance.now();
  const sp0 = season(), load0 = st.season === 'winter' ? st.snowAmt : sp0.snowLoad;
  // VIS-239: with a tree kit coming (main.js o.kitTrees + vegetation.trees.kit), today's trees are not built as a
  // stand-in: no impostor bake, nothing of theirs drawn or compiled. They are built (api.legacy) only when they are to be
  // seen: the kit failed or has nothing for the season
  const defer = !!(o.kitTrees && cfg.kit?.kinds);
  let apiRef = null; // (busy: VIS-267, a mean bake waits for the kit's season pictures: lazy/kittrees.js api.kitBusy)
  const kit = createTreeKit({ release: !!o.kitTrees?.release, renderer: o.renderer, atlasData: o.atlasData, snowLoad: load0, direction: detailedModel, season: st.season, impostor: imp, variants: cfg.variants ?? 2, a2c: a2c(), hq: cfg.hq, defer, busy: () => apiRef?.kitBusy?.(), atlasAgain: o.atlasAgain }); // WS-34: hq; WS-32: always the detailed model
  o.atlasData = null; // VIS-267 round 2: the kit's textures hold the atlas (and let it go while the kit draws: createTreeKit atlasOut)
  const snowLoad = () => (st.season === 'winter' ? st.snowAmt : season().snowLoad); // the snow level is the winter amount
  kit.setSnow(snowLoad());
  kit.U.uDensity.value = st.density + 0.0001;
  // r2 distance look (config, all optional): haze = { start, scale, max }, thin = { amount, from, to }
  if (cfg.haze) kit.U.uHaze.value.set(cfg.haze.start ?? 60, cfg.haze.scale ?? 700, cfg.haze.max ?? 0.5, 0);
  if (cfg.thin) kit.U.uThin.value.set(cfg.thin.amount ?? 0.25, cfg.thin.from ?? 450, cfg.thin.to ?? 1400, 0);
  let haze0 = null; // WS-42: the config's haze, restored by setHaze(null)
  const hazeTint = { c: new THREE.Color(), k: 0 }; // WS-82 grade.lowSun.haze: the haze leans toward this colour by k (0 = the fog's)
  const nVar = cfg.variants ?? 2;

  // per-tree instance data: matrix (yaw only, scale r,h,r), aTree = (variant, rank, seed, dom), tint
  const hash = (x, z) => { const s = Math.sin(x * 127.1 + z * 311.7) * 43758.5453; return s - Math.floor(s); };
  // WS-34 hq.tint = { value, hue, stand, standM }: wider per-tree value + warm/cool hue spread, and stands (value
  // noise over `standM` m) so neighbouring trees share a tone the way a real forest does. Absent = today's tint.
  const TINT = cfg.hq?.tint || null;
  const vnoise = (x, z) => { const i = Math.floor(x), j = Math.floor(z), f = x - i, g = z - j, u = f * f * (3 - 2 * f), w = g * g * (3 - 2 * g);
    return lerp(lerp(hash(i, j), hash(i + 1, j), u), lerp(hash(i, j + 1), hash(i + 1, j + 1), u), w); };
  const hqTint = (x, z, r0, r2, out, k) => {
    const sm = TINT.standM ?? 35, st1 = vnoise(x / sm, z / sm) - 0.5, st2 = vnoise(x / sm + 17.3, z / sm - 9.1) - 0.5;
    const v = 1 + (r2 - 0.5) * (TINT.value ?? 0) + st1 * (TINT.stand ?? 0) * 0.3;
    const h = ((r0 - 0.5) * 2 + st2 * (TINT.stand ?? 0)) * (TINT.hue ?? 0); // + warm (yellow-olive), - cool (blue-green)
    out[k] = Math.max(0.5, out[k] * v * (1 + h)); out[k + 1] = Math.max(0.5, out[k + 1] * v * (1 + h * 0.35)); out[k + 2] = Math.max(0.5, out[k + 2] * v * (1 - h));
  };
  const M = new Float32Array(N * 16), A = new Float32Array(N * 4), Cl = new Float32Array(N * 3);
  // VIS-199: the editor's frame plants live into `o.spare` slots at the end of the list (setPlanted), dormant until then
  // (rank 9: drawn at no amount). They have their own impostor cell and stay out of the near buckets (refill visits them).
  const SP = o.spare > 0 ? { from: N - o.spare, n: o.spare, used: 0 } : null;
  const isSpare = (i) => !!SP && i >= SP.from;
  const writeTree = (i, dormant = false) => {
    const x = T.x[i], z = T.z[i], r0 = hash(x + 0.31, z), r1 = hash(z, x + 0.77), r2 = hash(x * 1.3, z * 0.7);
    // T(x, y, z) * RotY(yaw) * S(r, h, r), column-major (as Matrix4.compose, without the per-tree objects)
    const a = r1 * 6.283, cs = Math.cos(a), sn = Math.sin(a), rr = T.r[i], o16 = i * 16;
    M[o16] = cs * rr; M[o16 + 2] = -sn * rr; M[o16 + 5] = T.h[i]; M[o16 + 8] = sn * rr; M[o16 + 10] = cs * rr;
    M[o16 + 12] = x; M[o16 + 13] = T.y[i]; M[o16 + 14] = z; M[o16 + 15] = 1;
    const si = T.s[i] > 2 ? 0 : T.s[i];
    // rank: understorey and small trees go first when thinning; canopy-dominant trees last (stable per position)
    const rank = dormant ? 9 : (T.dom[i] ? 0 : 0.3) + hash(x * 0.71 + 3.1, z * 1.37) * 0.7;
    A.set([si * nVar + Math.floor(r0 * nVar), rank, r2, T.dom[i] ? 1 : 0], i * 4);
    const v = 0.88 + r2 * 0.22;
    Cl[i * 3] = v * (0.97 + r0 * 0.06); Cl[i * 3 + 1] = v; Cl[i * 3 + 2] = v * (0.96 + r1 * 0.08);
    if (TINT) hqTint(x, z, r0, r2, Cl, i * 3);
  };
  for (let i = 0; i < N; i++) writeTree(i, isSpare(i));

  // WS-45: the far sun-shadow cascade (shadowCamera.userData.farCascade, shadows.js) draws every tree as its impostor:
  // the detailed near trees skip that pass and, for its draws only, the impostors stop handing over to them
  // (uLod.z = 0). Its texels are a metre or more, the near cascade keeps the detail; this halves the trees' shadow cost.
  const farPass = (sc) => sc?.userData?.farCascade === true;
  let lodZ = 1;
  const impBeforeShadow = (r, obj, cam, sc) => { const L = kit.U.uLod.value; if (farPass(sc)) { if (L.z !== 0) lodZ = L.z; L.z = 0; } else if (L.z === 0) L.z = lodZ; };
  const impAfterShadow = (r, obj, cam, sc) => { if (farPass(sc)) kit.U.uLod.value.z = lodZ; };
  const hiBeforeShadow = (r, obj, cam, sc) => { if (farPass(sc)) { const g = obj.geometry; obj.userData.n = g.instanceCount; g.instanceCount = 0; } };
  const hiAfterShadow = (r, obj, cam, sc) => { if (farPass(sc)) obj.geometry.instanceCount = obj.userData.n; };

  // impostor cells (all species in one mesh per cell)
  const quad = billboardQuad(); // WS-75: position only (vertex buffer budget)
  const cells = new Map();
  for (let i = 0; i < N; i++) { const k = isSpare(i) ? 'spare' : o.cellOf ? o.cellOf(T.x[i], T.z[i]) : 'all'; let l = cells.get(k); if (!l) cells.set(k, (l = [])); l.push(i); } // VIS-199: + the spare slots' own cell
  const impMeshes = [];
  // VIS-267 round 2 (phone memory): while a kit draws (!kit.live) nothing draws the impostors, so their instance data
  // (~92 bytes a tree: 6.5 MB at Aurora, + the far band's) are one-instance stubs, never uploaded; fillImp writes them
  // again from the per-tree arrays (M, A, Cl: kept, they are the working data) or the far band's source before today's
  // trees are shown (legacy). The kit reads the far band once (lazy/kittrees.js linkBand), so it is held only once the
  // kit is in (update). Invariant: filled while kit.live. Their GPU buffers keep their size: a refill after unlegacy
  // goes into them (version bump), never a stub (held meshes are skipped by every writer).
  const held = new WeakSet(), fillOf = new Map();
  const instArrays = (m) => { const g = m.geometry; return { ib: g.attributes.iM0.data, iC: g.attributes.iC, at: g.attributes.aTree }; };
  function holdImp(m) {
    if (held.has(m)) return;
    const { ib, iC, at } = instArrays(m);
    ib.array = new Float32Array(16); iC.array = new Float32Array(3); at.array = new Float32Array(4); // (counts stay: plain properties)
    m.userData.inst.matrix = ib.array; m.userData.inst.color = iC.array;
    held.add(m);
  }
  function fillImp(m) {
    if (!held.has(m)) return;
    held.delete(m);
    const { ib, iC, at } = instArrays(m), n = Math.max(m.userData.inst.count, 1); // (sharedInstances' sizes)
    ib.array = new Float32Array(n * 16); iC.array = new Float32Array(n * 3).fill(1); at.array = new Float32Array(m.userData.inst.count * 4);
    m.userData.inst.matrix = ib.array; m.userData.inst.color = iC.array;
    fillOf.get(m)(m.userData.inst, at.array);
    ib.needsUpdate = true; iC.needsUpdate = true; at.needsUpdate = true;
  }
  let impHeld = false; // every impostor mesh held (update: once the kit is in)
  for (const [key, ids] of cells) {
    // WS-75 perf: one mesh per cell, but all cells share ONE node build (gpu/instance.js sharedInstances; an InstancedMesh
    // per cell built the same impostor program 13 times + 12 shadow builds). Matrix = iM0..3, tint = iC, aTree as before.
    const mesh = sharedInstances(quad, kit.impMat, ids.length), g = mesh.geometry, I = mesh.userData.inst;
    const at = new THREE.InstancedBufferAttribute(new Float32Array(ids.length * 4), 4);
    g.setAttribute('aTree', at);
    const fill = (J, a4) => ids.forEach((id, k) => {
      J.matrix.set(M.subarray(id * 16, id * 16 + 16), k * 16);
      a4.set(A.subarray(id * 4, id * 4 + 4), k * 4);
      J.color.set(Cl.subarray(id * 3, id * 3 + 3), k * 3);
    });
    fillOf.set(mesh, fill);
    if (kit.live || !o.kitTrees?.release) fill(I, at.array); else holdImp(mesh); // (held from the start: never written)
    let x0 = Infinity, y0 = Infinity, z0 = Infinity, x1 = -Infinity, y1 = -Infinity, z1 = -Infinity;
    ids.forEach((id) => {
      const tx = T.x[id], ty = T.y[id], tz = T.z[id];
      if (tx < x0) x0 = tx; if (tx > x1) x1 = tx; if (tz < z0) z0 = tz; if (tz > z1) z1 = tz; if (ty < y0) y0 = ty; if (ty + T.h[id] > y1) y1 = ty + T.h[id];
    });
    const box = new THREE.Box3(new V3(x0, y0, z0), new V3(x1, y1, z1)).expandByScalar(4);
    g.boundingBox = box; g.boundingSphere = box.getBoundingSphere(new THREE.Sphere()); mesh.boundingBox = box; // WS-75 perf: culling reads the geometry's
    mesh.castShadow = o.castShadowCell ? o.castShadowCell(key) : true;
    mesh.receiveShadow = true;
    // WS-75: the impostor material draws its own sun shadow (no customDepthMaterial on WebGPU)
    mesh.onBeforeShadow = impBeforeShadow; mesh.onAfterShadow = impAfterShadow; // WS-45
    mesh.matrixAutoUpdate = false; mesh.visible = kit.live; // VIS-239: shown once today's trees are built (api.legacy)
    mesh.name = `trees-imp-${key}`;
    mesh.userData.ids = ids;
    o.scene.add(mesh);
    impMeshes.push(mesh);
  }

  // near set: one mesh per variant, refilled around the camera. gpu/instance.js sharedInstances (as the cells and the kit's
  // near trees): the matrices are the geometry attributes iM0..3 the shader places the trees by, the tint iC, one node build
  // for every variant (an InstancedMesh per variant built hiMat 6 times, and three's own instancing read the same matrices
  // as 4 more vertex attributes: 18 of WebGPU's 16, the program never built: MAPMYSTAY-VIEWER-22, trees-near-* / ws18-hi)
  const HI_CAP = cfg.hiCap ?? (o.mobile ? 4500 : 9000); // VIS-233: a phone's near set is ~30 % (PHONE_NEAR); its room stays half
  let hiMeshes = [];
  function makeHi() {
    hiMeshes.forEach((m) => { o.scene.remove(m); m.geometry.dispose(); });
    hiMeshes = kit.variants.map((v) => {
      const mesh = sharedInstances(v.geo, kit.hiMat, HI_CAP), g = mesh.geometry; // (shares the variant's interleaved buffer and index: clone() would split it into 6)
      g.setAttribute('aTree', new THREE.InstancedBufferAttribute(new Float32Array(HI_CAP * 4), 4));
      g.boundingSphere = v.geo.boundingSphere?.clone() ?? null; g.boundingBox = v.geo.boundingBox?.clone() ?? null;
      g.instanceCount = 0; mesh.visible = kit.live; mesh.castShadow = true; mesh.receiveShadow = true; // VIS-239: (refill shows the ones with trees once built)
      // WS-75: hiMat draws its own sun shadow
      mesh.onBeforeShadow = hiBeforeShadow; mesh.onAfterShadow = hiAfterShadow; // WS-45: not in the far shadow pass
      mesh.frustumCulled = false; mesh.matrixAutoUpdate = false;
      mesh.name = `trees-near-${v.name}`;
      o.scene.add(mesh);
      return mesh;
    });
    last.set(1e9, 0, 0);
  }
  const B = 40, buckets = new Map();
  const bkey = (bx, bz) => (bx + 4096) * 8192 + (bz + 4096); // numeric keys (faster than strings for 100k+ trees)
  const bucketIn = (i) => { const k = bkey(Math.floor(T.x[i] / B), Math.floor(T.z[i] / B)); let l = buckets.get(k); if (!l) buckets.set(k, (l = [])); l.push(i); return l; };
  for (let i = 0; i < (SP ? SP.from : N); i++) bucketIn(i); // (VIS-199: a spare slot joins its bucket once planted: setPlanted)
  const last = new V3(1e9, 0, 0);
  let nearR = 0, margin = 0;
  if (kit.live) makeHi(); // VIS-267: while a kit draws, the near set (shared variant geometry + ~2.5 MB of instance room on a phone) waits for api.legacy
  function refill(cam, Rq) {
    const n = new Array(kit.variants.length).fill(0), R2 = Rq * Rq;
    const b0x = Math.floor((cam.x - Rq) / B), b1x = Math.floor((cam.x + Rq) / B), b0z = Math.floor((cam.z - Rq) / B), b1z = Math.floor((cam.z + Rq) / B);
    let over = false;
    for (let bx = b0x; bx <= b1x; bx++) for (let bz = b0z; bz <= b1z; bz++) {
      const l = buckets.get(bkey(bx, bz)); if (!l) continue;
      for (const i of l) {
        if (A[i * 4 + 1] >= st.density) continue;
        const dx = T.x[i] - cam.x, dy = T.y[i] + T.h[i] * 0.5 - cam.y, dz = T.z[i] - cam.z;
        if (dx * dx + dy * dy + dz * dz >= R2) continue;
        const vi = A[i * 4], mesh = hiMeshes[vi], k = n[vi];
        if (k >= HI_CAP) { over = true; continue; }
        const I = mesh.userData.inst;
        I.matrix.set(M.subarray(i * 16, i * 16 + 16), k * 16);
        mesh.geometry.attributes.aTree.array.set(A.subarray(i * 4, i * 4 + 4), k * 4);
        I.color.set(Cl.subarray(i * 3, i * 3 + 3), k * 3);
        n[vi] = k + 1;
      }
    }
    hiMeshes.forEach((m, vi) => { m.geometry.instanceCount = n[vi]; m.visible = n[vi] > 0; markShared(m, n[vi], [m.geometry.attributes.aTree]); }); // WS-75 perf: upload the refilled instances only // WS-34: an empty variant skips its draw setup (main + shadow)
    last.copy(cam);
    return over;
  }
  const layerMs = Math.round(performance.now() - tLayer);
  const lod = { ...(cfg.lod || {}) };
  let far = null, canopyCol = null;
  const applyFar = () => {}; // r2: the far band is an impostor cell now; the rank test thins it like the near trees
  // the landscape's canopy tone = the baked forest's mean albedo with its snow share (cfg.far.canopyTone scales it)
  const applyCanopy = () => {
    if (!canopyCol || !kit.mean) return;
    const snow = kit.U.uSnow.value.x > 0.001 ? kit.mean.snow : 0;
    canopyCol.copy(kit.mean.albedo).lerp(new THREE.Color().copy(kit.U.uSnowCol.value).multiplyScalar(0.92), snow).multiplyScalar(cfg.far?.canopyTone ?? 1);
  };
  kit.onBake = applyCanopy;
  let lastA2C = a2c(), rank0 = null; // WS-58 rank0: the built ranks (setHidden)
  const api = {
    count: N, kit, lastSnowMs: 0, A, // WS-63 light: the per-tree data (aTree; A[i * 4 + 1] = rank), read by lazy/lightcast.js (only drawn trees darken the ground)
    v: 0, // map controls: + 1 whenever which trees are drawn changes (setHidden, setPlanted, setDensity): main.js remakes its tree pick list
    update(camera) {
      const U = kit.U;
      U.uCam.value.copy(camera.position);
      const fog = o.scene.fog; U.uFogOn.value = fog?.color ? 1 : 0; if (fog?.color) { U.uFogCol.value.copy(fog.color); if (hazeTint.k > 0) U.uFogCol.value.lerp(hazeTint.c, hazeTint.k); } // WS-75: the haze's horizon colour (was the shader's fogColor); WS-82: setHazeTint
      if (a2c() !== lastA2C) { lastA2C = a2c(); kit.setA2C(lastA2C); } // coverage mode only; the model never changes
      // VIS-267: built for a season without kit pictures or with failed ones, today's trees are freed once the kit has
      // taken over (its cross-fade done: lazy/kittrees.js hides them); a switch's cover waits for the kit's own pictures (seasonReady)
      if (kit.live && api.kitTrees?.on && U.uHeroFade.value.x >= 1) api.unlegacy();
      if (!kit.live && !impHeld && api.kitTrees && o.kitTrees?.release) { impMeshes.forEach(holdImp); impHeld = true; } // VIS-267 round 2: the kit is in (it has read the far band)
      if (!kit.live) return; // VIS-239: not built (the kit draws or is on its way: being slow brings up nothing, 2026-11-02)
      // near radius: lod.near metres on every tier (WS-32: no tier scaling, the detailed trees never pull in); x PHONE_NEAR on a phone (VIS-233)
      const R = (lod.near ?? 130) < 1 ? 0.02 : Math.max(30, (lod.near ?? 130) * (o.mobile ? PHONE_NEAR : 1)), band = Math.max(0.01, Math.min(R * 0.4, lod.band ?? 28)); // smoothstep edges never equal
      if (R !== nearR || camera.position.distanceToSquared(last) > margin * margin) {
        nearR = R; margin = Math.max(12, R * 0.15);
        let Rq = R + margin;
        // capacity: shrink the radius instead of dropping trees (each tree is drawn exactly once)
        for (let k = 0; k < 4 && refill(camera.position, Rq); k++) Rq *= 0.85;
        U.uLod.value.set(Math.min(R, Rq - margin), band, 1, 0);
      }
    },
    /** VIS-239: today's trees built and shown (the impostor bake; the near set fills at the next update). The last
     *  resort, never for something still loading (2026-11-02, the owner: they are heavier than the kit): lazy/kittrees.js
     *  for a season without its pictures or whose pictures failed, trees.js when the kit failed. true = built now. */
    legacy() {
      if (!kit.ensure()) return false;
      makeHi(); // VIS-267: on the variants as built now
      impMeshes.forEach(fillImp); impHeld = false; // VIS-267 round 2: their instance data written again before they are shown
      for (const m of impMeshes) m.visible = true;
      last.set(1e9, 0, 0); nearR = -1;
      o.kitTrees?.wake?.(); o.kitTrees?.shadowDirty?.();
      return true;
    },
    /** VIS-267: the kit took over again (lazy/kittrees.js, its cross-fade done): today's trees are freed (their
     *  pictures, atlas, variants, the near set); legacy() builds them again if needed. true = freed now. */
    unlegacy() {
      for (const m of impMeshes) m.visible = false;
      if (!kit.retire()) return false;
      hiMeshes.forEach((m) => { o.scene.remove(m); m.geometry.dispose(); }); hiMeshes = [];
      return true;
    },
    /** VIS-267: the forest's mean colour for a season the map can switch to (main.js, idle after Ready; `over` = that
     *  season's seasonParams), at the snow load the switch would bake with (as setSeason + setSnow below set it), so the
     *  switch bakes nothing. -> Promise<true when kept> */
    prebake(name, over = {}) {
      if (kit.live) return Promise.resolve(true); // (today's trees drawn: a switch bakes their pictures, its colour with them)
      const sp = seasonParams(name, over), x = Math.min(0.92, Math.max(0, name === 'winter' ? st.snowAmt : sp.snowLoad));
      return kit.prebake(sp, x > 0.001 && Math.abs(x - kit.bakedLoad) > 0.01 ? x : kit.bakedLoad);
    },
    /** QA: near radius in metres at High (0.01 = impostors only, 1e5 = 3D models only). */
    setNear(m, band) { lod.near = m; if (band != null) lod.band = band; nearR = -1; },
    setDensity(v) { st.density = clamp01(v); kit.U.uDensity.value = st.density + 0.0001; last.set(1e9, 0, 0); applyFar(); save(); api.v++; }, // WS-40: the viewer's Amount slider
    // WS-58: the trees a fine-tune touch takes away, live (the editor's frame, lazy/fine.js): test(x, z) true = hidden.
    // A hidden tree's rank goes past every amount, so neither the near set nor the impostors draw it; null = all back.
    // (WS-88) `box` [x0, z0, x1, z1]: only the trees inside it are tested again, the others keep what the last call gave
    // them (the caller's promise: the test changed only there)
    setHidden(test, box = null) {
      rank0 ||= Float32Array.from({ length: N }, (_, i) => A[i * 4 + 1]);
      for (let i = 0; i < N; i++) {
        const x = T.x[i], z = T.z[i];
        if (box && !(x >= box[0] && x <= box[2] && z >= box[1] && z <= box[3])) continue;
        A[i * 4 + 1] = test?.(x, z) ? 9 : rank0[i];
      }
      for (const m of impMeshes) if (m.userData.ids && !held.has(m)) { const at = m.geometry.attributes.aTree; m.userData.ids.forEach((id, k) => { at.array[k * 4 + 1] = A[id * 4 + 1]; }); at.needsUpdate = true; }
      last.set(1e9, 0, 0); api.v++;
    },
    // VIS-199: the editor's frame plants live. The caller wrote the first `n` spare slots' trees into T (x, z, y, h, r, s,
    // dom); they are drawn from now on (their bucket, their impostor), the rest go dormant. Call setHidden after it (a
    // touch may take a planted tree away). Returns how many are shown (at most the slots there are). onPlanted: kittrees.js.
    spare: SP,
    setPlanted(n) {
      if (!SP) return 0;
      n = Math.max(0, Math.min(n, SP.n));
      rank0 ||= Float32Array.from({ length: N }, (_, i) => A[i * 4 + 1]);
      SP.bk ||= [];
      const upto = Math.max(n, SP.used);
      for (let j = 0; j < upto; j++) {
        const i = SP.from + j, l = SP.bk[j];
        if (l) { l.splice(l.indexOf(i), 1); SP.bk[j] = null; }
        writeTree(i, j >= n); rank0[i] = A[i * 4 + 1];
        if (j < n) SP.bk[j] = bucketIn(i);
      }
      SP.used = n;
      const m = impMeshes.find((q) => q.name === 'trees-imp-spare'), I = m?.userData.inst, g = m?.geometry;
      if (m) {
        const at = g.attributes.aTree, box = new THREE.Box3(), put = !held.has(m); // (VIS-267 round 2: a held mesh is written whole when shown: fillImp)
        for (let k = 0; k < upto; k++) {
          const id = SP.from + k;
          if (put) { I.matrix.set(M.subarray(id * 16, id * 16 + 16), k * 16); at.array.set(A.subarray(id * 4, id * 4 + 4), k * 4); I.color.set(Cl.subarray(id * 3, id * 3 + 3), k * 3); }
          if (k < n) box.expandByPoint(new V3(T.x[id], T.y[id], T.z[id])).expandByPoint(new V3(T.x[id], T.y[id] + T.h[id], T.z[id]));
        }
        if (box.isEmpty()) box.setFromCenterAndSize(new V3(T.x[SP.from], T.y[SP.from], T.z[SP.from]), new V3(1, 1, 1));
        box.expandByScalar(4); g.boundingBox = box; g.boundingSphere = box.getBoundingSphere(new THREE.Sphere()); m.boundingBox = box;
        if (put) { g.attributes.iM0.data.needsUpdate = true; g.attributes.iC.needsUpdate = true; at.needsUpdate = true; }
      }
      api.onPlanted?.(upto);
      last.set(1e9, 0, 0); api.v++;
      return n;
    },
    /** Snow level by id (levels from vegetation.snow); re-bakes the impostors' snow share (~100 ms). */
    setSnowLevel(id) {
      const L = levels.find((l) => l.id === id); if (!L) return;
      st.snowLevel = L.id; st.snowAmt = L.load; st.snow = L.load > 0.001;
      const t0 = performance.now(); kit.setSnow(snowLoad()); api.lastSnowMs = performance.now() - t0;
      applyCanopy(); api.onSnowGrade?.(levelGrade()); save();
    },
    /** Fine amount 0..1. commit = false while dragging: the near trees follow at once, the impostors re-bake on release. */
    setSnowAmount(v, commit = true) {
      st.snowAmt = Math.min(1, Math.max(0, v)); st.snow = st.snowAmt > 0.001;
      const L = levels.find((l) => Math.abs(l.load - st.snowAmt) < 0.005); st.snowLevel = L ? L.id : 'custom';
      if (!commit) { kit.U.uSnow.value.x = Math.min(0.92, snowLoad()); return; }
      const t0 = performance.now(); kit.setSnow(snowLoad()); api.lastSnowMs = performance.now() - t0;
      applyCanopy(); api.onSnowGrade?.(levelGrade()); save();
    },
    snowGrade: levelGrade,
    setSnow(on) { this.setSnowLevel(on ? (lvl0.load > 0 ? lvl0.id : (levels.find((l) => l.load > 0) || lvl0).id) : (levels.find((l) => l.load <= 0.001) || levels[0]).id); }, // compat: on/off
    /** The landscape's far forest (one InstancedMesh, instances in random order): the amount thins it too. */
    /** r2: the landscape's far band (WS-17) is drawn by this layer as impostors (same atlas, snow, haze, density
     *  rule), and its canopy shading takes the same haze and the forest's mean colour, so neither hand-over shows. */
    linkFar(mesh, land) {
      if (mesh?.isInstancedMesh && !far) {
        const n = mesh.count, src = mesh.instanceMatrix.array; // (VIS-267 round 2: kept by the landscape's mesh, hidden here: the far band's source when it is written again, fillImp)
        const farFill = (J, A2) => {
          const M2 = J.matrix, C2 = J.color;
          for (let i = 0; i < n; i++) {
            const o16 = i * 16, sx = Math.hypot(src[o16], src[o16 + 1], src[o16 + 2]) || 1, sy = Math.hypot(src[o16 + 4], src[o16 + 5], src[o16 + 6]) || 1;
            const x = src[o16 + 12], y = src[o16 + 13], z = src[o16 + 14], cs = src[o16] / sx, sn = -src[o16 + 2] / sx;
            // size and species drawn from the survey trees (the same distribution as the ring inside), not the card's
            const j = Math.floor(hash(x * 0.913, z * 1.27) * N) % N, h = T.h[j], r = T.r[j];
            M2[o16] = cs * r; M2[o16 + 2] = -sn * r; M2[o16 + 5] = h; M2[o16 + 8] = sn * r; M2[o16 + 10] = cs * r; M2[o16 + 12] = x; M2[o16 + 13] = y; M2[o16 + 14] = z; M2[o16 + 15] = 1;
            const si = T.s[j] > 2 ? 0 : T.s[j], dom = hash(z * 0.53, x * 0.91) < 0.55; void sx; void sy;
            A2.set([si * nVar + Math.floor(hash(x + 0.31, z) * nVar), (dom ? 0 : 0.3) + hash(x * 0.71 + 3.1, z * 1.37) * 0.7, hash(x * 1.3, z * 0.7), dom ? 1 : 0], i * 4);
            const v = 0.88 + hash(x * 1.3, z * 0.7) * 0.22; C2[i * 3] = v; C2[i * 3 + 1] = v; C2[i * 3 + 2] = v;
            if (TINT) hqTint(x, z, hash(x + 0.31, z), hash(x * 1.3, z * 0.7), C2, i * 3); // WS-34 hq: the far band matches
          }
        };
        // WS-75 perf: as the cells (gpu/instance.js sharedInstances): the far band shares their node build
        const fm = sharedInstances(quad, kit.impMat, n), g = fm.geometry, at = new THREE.InstancedBufferAttribute(new Float32Array(n * 4), 4);
        g.setAttribute('aTree', at);
        farFill(fm.userData.inst, at.array); // (written now whatever the kit: lazy/kittrees.js linkBand reads it; held once the kit is in, update)
        fillOf.set(fm, farFill); impHeld = false;
        const M2 = fm.userData.inst.matrix, fb = new THREE.Box3(); for (let i = 0; i < n; i++) fb.expandByPoint(new V3(M2[i * 16 + 12], M2[i * 16 + 13], M2[i * 16 + 14]));
        fb.expandByScalar(20); g.boundingBox = fb; g.boundingSphere = fb.getBoundingSphere(new THREE.Sphere()); fm.boundingBox = fb;
        fm.castShadow = false; fm.receiveShadow = true; fm.matrixAutoUpdate = false; fm.visible = kit.live; fm.name = 'trees-imp-farband'; // VIS-239: (api.legacy shows it)
        o.scene.add(fm); impMeshes.push(fm);
        mesh.visible = false; // the cone cards are replaced
        far = { mesh: fm, n };
      }
      if (land?.material && land.userData?.rim?.uTreeCol && !land.userData.ws18) {
        land.userData.ws18 = true;
        api.patchLand = (m) => { // WS-41: also for the far ground's other season materials (live season switch)
          // WS-75: the far ground is a node material; landscape.js keeps its canopy share (t x clamp(vTree x 1.25), the
          // share that mixes in uTreeCol) as seam(material).canopyK. The haze goes on after its fog and rim (gpu/chain.js
          // fog hook, stage 20). Without that node the far ground keeps its own look.
          const k = SEAMS.get(m)?.canopyK;
          if (!k) { if (!api.patchLandWarned) { api.patchLandWarned = true; console.warn('[trees] far ground: no canopy share (seam canopyK), canopy haze left out'); } return; }
          if (hazedLand.has(m)) return;
          hazedLand.add(m);
          onFog(m, (out) => hazeOver(out, kit.U, k), 20);
        };
        api.patchLand(land.material);
        canopyCol = land.userData.rim.uTreeCol.value;
        applyCanopy();
      }
    },
    setSnowLoad(v) { kit.setSnow(v); },
    // WS-41: over = that season's vegetation.trees.seasonParams (live switch). applyCanopy last: the season's bake runs
    // before the snow load changes, so the far canopy tone would keep the previous season's snow share (a frosted
    // horizon in summer after a switch from winter).
    setSeason(s, over) { st.season = s; if (over !== undefined) cfg.seasonParams = over; kit.setSeason(season()); kit.setSnow(snowLoad()); applyCanopy(); },
    /** WS-42: aerial perspective per season (resort.grade.haze = { start, scale, max }); null = the config's own. */
    // h.k (picture parameter hazeK) scales the haze's maximum; at most 0.6 so the far forest never turns into a wall
    setHaze(h) { if (!haze0) haze0 = kit.U.uHaze.value.clone(); kit.U.uHaze.value.set(h?.start ?? haze0.x, h?.scale ?? haze0.y, h?.k != null && h.k !== 1 ? Math.min(0.6, (h.max ?? haze0.z) * h.k) : h?.max ?? haze0.z, 0); },
    /** WS-82: the haze's colour leans toward `color` by k (0..1; 0 = the scene fog's colour, as before). */
    setHazeTint(color, k = 0) { hazeTint.k = k > 0 && color ? k : 0; if (hazeTint.k) hazeTint.c.copy(color); },
    setFocus(v, radius = cfg.readability?.focusRadius ?? 6) {
      if (!v || cfg.readability?.focusFade === false) kit.U.uFocus.value.w = 0;
      else kit.U.uFocus.value.set(v.x, v.y, v.z, radius);
    },
    settings: () => ({ ...st, direction: kit.direction, model: detailedModel, snowLevels: levels.map(({ id, label, load }) => ({ id, label, load })), snowUI: SL.ui !== false && st.season === 'winter' }), // WS-41: no snow row outside winter (live season switch)
    season,
    stats: () => ({ trees: N, timing: { ...kit.timing, layer: layerMs }, cells: impMeshes.length, near: hiMeshes.map((m) => m.geometry.instanceCount), nearR, variants: kit.stats(), impostor: kit.bakeInfo, bakeMs: kit.lastBakeMs }),
    meshes: () => ({ imp: impMeshes, hi: hiMeshes }),
  };
  // smooth start: api.ready = the start's trees are built, uploaded and compiled: 'kit' | 'failed' (today's trees, the
  // last resort) | 'none' (no kit set); main.js waits for it under its cover. o.kitTrees covered() = the cover is up: they
  // take over at once (no cross-fade nobody would see).
  apiRef = api;
  api.ready = Promise.resolve('none');
  // 2026-10-19 tree kits (vegetation.trees.kit; lazy/kittrees.js): every survey tree as its kit tree, in every season;
  // today's trees draw only where it has nothing (it failed, a season it has no pictures for). o.kitTrees = { js, files, warm, wake, shadowDirty, covered } (main.js). Skill: trees.md "Tree kits on the map".
  if (o.kitTrees && cfg.kit?.kinds) {
    api.ready = o.kitTrees.js.then((m) => m.attachKit({ api, kit, cfg, st, T, N, M, A, nVar, scene: o.scene, renderer: o.renderer, mobile: o.mobile, imp: impMeshes, farPass, ...o.kitTrees }))
      .then(() => 'kit', (e) => { console.warn('[kit trees] not shown', e); import('./errors.js').then((r) => r.reportError(e, { area: 'kit-trees', step: 'attach' })).catch(() => {}); o.kitTrees.files?.stop?.(); api.legacy(); return 'failed'; }); // errors:ignore: reporting is best effort; a bug, no words to a visitor; today's trees, so a map is not left with none
  }
  return api;
}
